import { api } from "@/services/apiClient";
import type { DashboardKpi } from "@/types";

export type KeywordItem = {
  id: string;
  keyword: string;
  volume: number;
  difficulty: number;
  intent: string;
  cluster?: string;
  seed?: string;
  created_at: string;
  meta?: {
    source?: string;
    clicks?: number;
    impressions?: number;
    ctr?: number;
    position?: number;
    volume_meaning?: string;
    difficulty_meaning?: string;
    synced_at?: string;
    bucket?: string;
    opportunity?: {
      type?: string;
      priority?: string;
      label?: string;
      action?: string;
      content_type?: string;
    };
    also_seen_in?: string[];
  };
};

export type KeywordOpportunity = {
  keyword: string;
  opportunity?: {
    type?: string;
    priority?: string;
    label?: string;
    action?: string;
    content_type?: string;
  };
  clicks?: number;
  impressions?: number;
  ctr?: number;
  position?: number;
  intent?: string;
  source?: string;
  synced_at?: string;
};

export type KeywordCluster = {
  id: string;
  slug: string;
  name: string;
  pillar_keyword: string;
  keyword_count: number;
  total_volume: number;
  avg_difficulty: number;
  intents: string[];
  keywords: Array<{
    id?: string;
    keyword?: string;
    volume?: number;
    difficulty?: number;
    intent?: string;
  }>;
  mode?: string;
};

export type ContentDraft = {
  id: string;
  title: string;
  channel: string;
  brief: string;
  tone: string;
  status: string;
  body: string;
  created_at: string;
  updated_at: string;
  meta?: {
    promoted_platforms?: string[];
    promoted_social_ids?: string[];
    promoted_social_id?: string;
    publish_at?: string;
    [key: string]: unknown;
  };
};

export type MarketingCampaign = {
  id: string;
  name: string;
  channels: string[];
  budget: number;
  status: string;
  created_at: string;
  updated_at: string;
  objective?: string;
  audience?: string;
  starts_at?: string | null;
  ends_at?: string | null;
  target_leads?: number | null;
  primary_kpi?: string;
  utm?: Record<string, string>;
  brief?: Record<string, unknown> | null;
  meta?: Record<string, unknown>;
};

export type CampaignHubPayload = {
  ok: boolean;
  campaign: MarketingCampaign;
  assets: {
    social: Array<Record<string, unknown>>;
    drafts: Array<Record<string, unknown>>;
    ads: Array<Record<string, unknown>>;
  };
  checklist: {
    items: Array<{
      id: string;
      label: string;
      done: boolean;
      count?: number;
      optional?: boolean;
    }>;
    ready_score: number;
    ready_to_launch: boolean;
    required_done: number;
    required_total: number;
  };
  performance: {
    planned_budget: number;
    ad_daily_budget_sum: number;
    budget_used_pct: number;
    assets: {
      social_total: number;
      social_published: number;
      social_scheduled: number;
      social_draft: number;
      drafts: number;
      ads: number;
    };
    target_leads?: number | null;
    note?: string;
  };
  calendar: {
    days: Record<string, Array<Record<string, unknown>>>;
    counts: { days: number; items: number; unscheduled: number };
  };
  utm: { params: Record<string, string>; url: string };
  linkable: {
    social: Array<{ id: string; label: string; status?: string }>;
    drafts: Array<{ id: string; label: string; status?: string }>;
    ads: Array<{ id: string; label: string; status?: string }>;
  };
};

export type BiPersona = {
  name: string;
  role?: string;
  goals: string[];
  pains?: string[];
  channels: string[];
  message?: string;
};

export type AmosSnapshotProvenance = {
  source?: string;
  mode?: string;
  ok?: boolean;
  limit?: number;
};

export type AmosSnapshot = {
  generated_at?: string;
  tenant_id?: string;
  disclaimer?: string;
  provenance?: Record<string, AmosSnapshotProvenance>;
  inventory?: {
    campaigns?: {
      total?: number;
      by_status?: Record<string, number>;
      total_budget?: number;
      active?: Array<Record<string, unknown>>;
    };
    keywords?: {
      total?: number;
      by_intent?: Record<string, number>;
      top_by_volume?: Array<Record<string, unknown>>;
      avg_difficulty?: number;
    };
    content?: {
      total?: number;
      by_status?: Record<string, number>;
      by_channel?: Record<string, number>;
      recent?: Array<Record<string, unknown>>;
    };
    competitors?: {
      count?: number;
      items?: Array<Record<string, unknown>>;
      recent_events?: Array<Record<string, unknown>>;
    };
    seo?: {
      audit_count?: number;
      latest?: Record<string, unknown> | null;
      avg_score?: number | null;
    };
    ads_social?: {
      ads_total?: number;
      ads_active?: number;
      ads_daily_budget_sum?: number;
      social_by_status?: Record<string, number>;
    };
    recommendations_open?: Array<Record<string, unknown>>;
  };
  crm?: {
    ok?: boolean;
    total?: number;
    status_counts?: Record<string, number>;
    recent?: Array<Record<string, unknown>>;
    message?: string;
    source?: string;
  };
  roi?: {
    totals?: { spend?: number; revenue?: number; roas?: number };
    providers?: Array<Record<string, unknown>>;
    mode?: string;
  };
  observability?: Record<string, number>;
  signals_for_swot?: {
    strengths?: string[];
    weaknesses?: string[];
    opportunities?: string[];
    threats?: string[];
  };
};

export type BiReportPayload = {
  mode?: string;
  disclaimer?: string;
  sections?: string[];
  company?: string;
  industry?: string;
  strengths?: string[];
  weaknesses?: string[];
  opportunities?: string[];
  threats?: string[];
  personas?: BiPersona[];
  company_profile?: Record<string, unknown>;
  website_signals?: Record<string, unknown>;
  business_model?: Record<string, unknown>;
  icp?: Record<string, unknown>;
  swot?: {
    strengths?: string[];
    weaknesses?: string[];
    opportunities?: string[];
    threats?: string[];
  };
  product_intelligence?: Record<string, unknown>;
  industry_intelligence?: Record<string, unknown>;
  competitor_watch?: {
    count?: number;
    hint?: string;
    items?: Array<Record<string, unknown>>;
  };
  revenue_model?: Record<string, unknown>;
  marketing_strategy?: Record<string, unknown>;
  growth_strategy?: Record<string, unknown>;
  next_actions?: Array<{ title?: string; detail?: string; path?: string }>;
  amos_links?: Array<{ label?: string; path?: string }>;
  amos_snapshot?: AmosSnapshot;
  first_party?: AmosSnapshot;
  ai_narrative?: string;
  ai_playbook?: string;
  ai_meta?: Record<string, unknown>;
  [key: string]: unknown;
};

export type BiReport = {
  id: string;
  report_type: string;
  title: string;
  summary: string;
  payload: BiReportPayload;
  created_at: string;
};

export type Competitor = {
  id: string;
  name: string;
  domain: string;
  notes: string;
  seo_score: number;
  pricing_tier: string;
  strengths: string[];
  weaknesses: string[];
  meta?: {
    monitoring_enabled?: boolean;
    last_monitored_at?: string;
    last_snapshot?: Record<string, unknown>;
    mode?: string;
    [key: string]: unknown;
  };
};

export type CompetitorEvent = {
  id: string;
  competitor_id: string;
  competitor_name: string;
  domain: string;
  event_type: "baseline" | "change" | "no_change" | string;
  summary: string;
  changes: Array<{ field: string; before: unknown; after: unknown }>;
  snapshot?: Record<string, unknown>;
  meta?: Record<string, unknown>;
  created_at: string;
};

export type CompetitorMonitorResult = {
  ok: boolean;
  scanned: number;
  changes: number;
  baselines: number;
  results: Array<Record<string, unknown>>;
  mode?: string;
  disclaimer?: string;
  queued?: boolean;
  job?: Record<string, unknown>;
};

export type SeoAudit = {
  id: string;
  url: string;
  score: number;
  issues: Array<{ severity: string; code?: string; message?: string; title?: string }>;
  recommendations: string[];
  meta?: Record<string, unknown>;
  mode?: string;
};

export type SeoMetaDraft = {
  ok?: boolean;
  url: string;
  keyword: string;
  title: string;
  meta_description: string;
  h1: string;
  schema?: Record<string, unknown>;
  faq?: Array<{ q: string; a: string }>;
  mode?: string;
  disclaimer?: string;
};

export type SocialPost = {
  id: string;
  platform: string;
  caption: string;
  hashtags: string[];
  status: string;
  scheduled_at?: string | null;
  meta?: {
    topic?: string;
    published_at?: string;
    publish_mode?: string;
    vendor_post_id?: string;
    live_attempt?: Record<string, unknown>;
    publish_result?: {
      ok?: boolean;
      mode?: string;
      reason?: string;
      api?: string;
      at?: string;
      retry?: boolean;
      force?: boolean;
      vendor_post_id?: string;
    };
    publish_history?: Array<{
      ok?: boolean;
      mode?: string;
      reason?: string;
      api?: string;
      at?: string;
      retry?: boolean;
      force?: boolean;
      vendor_post_id?: string;
    }>;
    [key: string]: unknown;
  };
};

export type AdCampaign = {
  id: string;
  name: string;
  network: string;
  objective: string;
  daily_budget: number;
  status: string;
  creative: string;
  targeting?: {
    geo?: string[];
    languages?: string[];
    age_min?: number | null;
    age_max?: number | null;
    genders?: string[];
    audiences?: string[];
    keywords?: string[];
    exclusions?: string[];
  };
  schedule?: { starts_at?: string | null; ends_at?: string | null };
  budget_plan?: {
    type?: string;
    currency?: string;
    daily_budget?: number;
    lifetime_budget?: number | null;
  };
  hierarchy?: {
    ad_groups?: Array<{
      id: string;
      name: string;
      status: string;
      bid_strategy?: string;
      ads?: Array<{
        id: string;
        name: string;
        status: string;
        headline?: string;
        description?: string;
        final_url?: string;
        cta?: string;
        creative_ids?: string[];
      }>;
    }>;
  };
  google_sync?: {
    status?: string;
    mode?: string;
    external_campaign_id?: string | null;
    last_sync_at?: string | null;
    last_error?: string | null;
  };
  meta_sync?: {
    status?: string;
    mode?: string;
    external_campaign_id?: string | null;
    last_sync_at?: string | null;
    last_error?: string | null;
  };
  linkedin_sync?: {
    status?: string;
    mode?: string;
    external_campaign_id?: string | null;
    last_sync_at?: string | null;
    last_error?: string | null;
  };
  vendor_sync?: {
    status?: string;
    mode?: string;
    external_campaign_id?: string | null;
    last_sync_at?: string | null;
    last_error?: string | null;
  };
  budget_rules?: {
    max_daily_spend?: number | null;
    pause_on_overspend?: boolean;
    alert_at_pct?: number;
    alerts?: Array<{ at?: string; type?: string; message?: string }>;
  };
  approval?: {
    required?: boolean;
    approved?: boolean;
    approved_at?: string | null;
    approved_by?: string | null;
    note?: string | null;
  };
  metrics_snapshot?: Record<string, unknown>;
  optimization_event?: string;
  utm?: Record<string, string>;
  brief?: Record<string, unknown> | null;
  counts?: { ad_groups?: number; ads?: number; creatives?: number; keywords?: number };
  updated_at?: string;
};

export type AdHubPayload = {
  ok: boolean;
  ad: AdCampaign;
  creatives: Creative[];
  linkable_creatives: Array<{ id: string; label: string; kind?: string; status?: string }>;
  checklist: {
    items: Array<{
      id: string;
      label: string;
      done: boolean;
      required?: boolean;
      optional?: boolean;
      count?: number;
    }>;
    required_total: number;
    required_done: number;
    ready_score: number;
    ready_to_launch: boolean;
  };
  performance: {
    daily_budget: number;
    estimated_weekly_spend: number;
    metrics?: {
      spend?: number;
      clicks?: number;
      impressions?: number;
      conversions?: number;
      revenue?: number;
      roas?: number;
      source?: string;
      as_of?: string;
      pacing_pct?: number | null;
    };
    assets: { ad_groups: number; ads: number; creatives: number; keywords: number };
    google_sync: Record<string, unknown>;
    note?: string;
  };
  google_readiness?: {
    oauth_connected?: boolean;
    customer_id?: string | null;
    mode?: string;
    detail?: string;
  } | null;
  network_readiness?: {
    oauth_connected?: boolean;
    mode?: string;
    detail?: string;
    provider?: string;
    network?: string;
    ad_account_id?: string | null;
    customer_id?: string | null;
  } | null;
  meta_readiness?: { oauth_connected?: boolean; mode?: string; detail?: string } | null;
  linkedin_readiness?: { oauth_connected?: boolean; mode?: string; detail?: string } | null;
  utm?: Record<string, string>;
  budget_rules?: {
    max_daily_spend?: number | null;
    pause_on_overspend?: boolean;
    alert_at_pct?: number;
    alerts?: Array<{ at?: string; type?: string; message?: string }>;
  };
  approval?: {
    required?: boolean;
    approved?: boolean;
    approved_at?: string | null;
    approved_by?: string | null;
    note?: string | null;
  };
};

export type Creative = {
  id: string;
  kind: string;
  title: string;
  format: string;
  brief: string;
  status: string;
  script: string;
  preview_url?: string | null;
  meta?: Record<string, unknown>;
};

export type CreativeExport = {
  ok: boolean;
  creative_id: string;
  format: string;
  filename: string;
  content: string;
  status?: string;
  mode?: string;
  disclaimer?: string;
};

export type ImageGenProvider = {
  id: string;
  label: string;
  description: string;
  billing: string;
  live_enabled: boolean;
};

export type ImageGenResult = {
  ok: boolean;
  provider: string;
  provider_label?: string;
  billing?: string;
  live_enabled: boolean;
  title: string;
  brief?: string;
  format: string;
  brand_voice: string;
  prompt: string;
  negative_prompt: string;
  notes: string;
  placeholder_url: string;
  mode: string;
  source?: string;
  disclaimer?: string;
  creative?: Creative;
};

export type VideoScriptBeat = {
  id: string;
  label: string;
  start_s: number;
  end_s: number;
  visual?: string;
  narration?: string;
  on_screen_text?: string;
};

export type VideoScriptTemplate = {
  id: string;
  label: string;
  description: string;
  target_seconds: number;
  beat_count: number;
};

export type VideoScriptResult = {
  ok: boolean;
  format: string;
  format_label?: string;
  target_seconds?: number;
  title: string;
  brief?: string;
  brand_voice: string;
  beats: VideoScriptBeat[];
  script: string;
  mode: string;
  source?: string;
  disclaimer?: string;
  creative?: Creative;
};

export type LandingBlock = {
  id: string;
  type: string;
  props: Record<string, unknown>;
};

export type LandingTemplate = {
  id: string;
  label: string;
  goal: string;
  description: string;
  default_slug_hint?: string;
};

export type LandingAnalytics = {
  views?: number;
  submissions?: number;
  conversion_rate?: number;
  last_event?: string;
  last_event_at?: string;
};

export type LandingPage = {
  id: string;
  name: string;
  slug: string;
  headline: string;
  status: string;
  template: string;
  html_outline: string;
  blocks?: LandingBlock[];
  meta?: Record<string, unknown>;
  utm?: Record<string, string>;
  seo?: Record<string, unknown>;
  analytics?: LandingAnalytics;
  campaign_id?: string | null;
  goal?: string;
  public_url?: string;
  utm_url?: string;
  block_count?: number;
  updated_at?: string;
};

export type LandingPageExport = {
  ok: boolean;
  page_id: string;
  format: string;
  filename: string;
  content: string;
  status?: string;
  mode?: string;
  disclaimer?: string;
};

export type LandingBlockType = {
  id: string;
  label: string;
  description: string;
};

export type LandingPreview = {
  ok: boolean;
  page_id?: string;
  slug?: string;
  content: string;
  block_count?: number;
  mode?: string;
  disclaimer?: string;
  utm_url?: string;
};

export type LandingHub = {
  ok: boolean;
  page: LandingPage;
  templates: LandingTemplate[];
  readiness: LandingReadiness;
  utm_url?: string;
  public_url?: string;
};

export type LandingLeadResult = {
  ok: boolean;
  crm: Record<string, unknown>;
  page_id: string;
  tracked?: boolean;
};

export type LandingPublishResult = {
  ok: boolean;
  status?: string;
  public_url?: string;
  utm_url?: string;
  page?: LandingPage;
  message?: string;
  error?: string;
  readiness?: LandingReadiness;
};

export type LandingReadinessCheck = {
  id: string;
  label: string;
  ok: boolean;
  severity: string;
  message: string;
};

export type LandingReadiness = {
  ok: boolean;
  page_id?: string;
  ready: boolean;
  host_ready: boolean;
  summary: { pass: number; fail: number; warn: number; total: number };
  checks: LandingReadinessCheck[];
  mode?: string;
  disclaimer?: string;
};

export type RecommendationHistoryEntry = {
  action: string;
  from_status?: string;
  to_status?: string;
  at: string;
  note?: string;
  snooze_days?: number;
};

export type Recommendation = {
  id: string;
  category: string;
  title: string;
  rationale: string;
  impact: string;
  effort: string;
  status: string;
  meta?: {
    last_action?: string;
    last_action_at?: string;
    last_note?: string;
    snoozed_until?: string | null;
    snooze_days?: number;
    action_history?: RecommendationHistoryEntry[];
    [key: string]: unknown;
  };
};

export type RecommendationAction = "accept" | "dismiss" | "snooze" | "complete" | "reopen";

export type RecommendationsResponse = {
  ok?: boolean;
  items: Recommendation[];
  counts?: Record<string, number>;
  snooze_options?: number[];
};

export type Workflow = {
  id: string;
  name: string;
  status: string;
  trigger: string;
  steps: Array<{ id: string; type: string; label: string }>;
};

export type Integration = {
  id: string;
  provider: string;
  status: string;
  last_sync_at?: string | null;
  config?: Record<string, unknown>;
};

export type MarketingSettings = {
  id: string;
  brand_voice: string;
  default_locale: string;
  timezone: string;
  notifications_email: boolean;
  feature_flags: Record<string, boolean>;
  meta?: {
    alert_email?: string;
    alert_user_id?: string;
    alert_webhook_url?: string;
    alert_channels?: string[];
    [key: string]: unknown;
  };
  alert_notifications?: boolean;
  alert_email?: string;
  alert_user_id?: string;
  alert_webhook_url?: string;
  quiet_hours?: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  quiet_hours_timezone?: string;
  quiet_hours_strict?: boolean;
  publish_day_quiet_skip?: boolean;
  /** Generation mode reported by the studio API (e.g. "typed", "ai"). */
  ai_mode?: string;
};

export type AnalyticsSummary = {
  series: Array<{ day: string; leads: number; spend: number; sessions: number }>;
  funnel: Array<{ stage: string; value: number }>;
  channels: Array<{ channel: string; share: number }>;
};

export type WebsiteScanCheck = {
  id: string;
  ok: boolean;
  detail: string;
};

export type WebsiteScan = {
  url: string;
  score: number;
  performance: number;
  accessibility: number;
  seo: number;
  best_practices: number;
  issues: Array<{ severity: string; title: string; fix: string }>;
  checks?: WebsiteScanCheck[];
  html_signals?: {
    title?: string;
    meta_description_length?: number;
    h1_count?: number;
    images?: number;
    images_missing_alt?: number;
  };
  scanned_at: string;
  scanner?: string;
  mode?: string;
  fetch_status?: string;
  disclaimer?: string;
};

export type WebsiteScannerInfo = { id: string; label: string };

export type HelpArticle = { id: string; title: string; body: string };

export async function fetchDashboardKpis(): Promise<DashboardKpi[]> {
  try {
    const { data } = await api.get("/api/marketing/dashboard/kpis");
    if (Array.isArray(data?.items)) return data.items as DashboardKpi[];
    if (Array.isArray(data)) return data as DashboardKpi[];
  } catch {
    /* fall through */
  }
  return [
    { id: "reach", label: "Reach (30d)", value: "128400", delta: "+12.4%", trend: "up" },
    { id: "leads", label: "Marketing Leads", value: "842", delta: "+8.1%", trend: "up" },
    { id: "cac", label: "Blended CAC", value: "$42", delta: "-3.2%", trend: "down" },
    { id: "roas", label: "Paid ROAS", value: "3.6x", delta: "+0.4", trend: "up" },
  ];
}

export type DashboardWorkQueueItem = {
  id: string;
  source: "approval" | "attention" | "recommendation" | string;
  severity: string;
  title: string;
  detail?: string | null;
  path?: string | null;
  kind?: string | null;
};

export type DashboardWorkQueue = {
  ok: boolean;
  tenant_id?: string;
  items: DashboardWorkQueueItem[];
  count: number;
  total_candidates?: number;
  summary: {
    approvals: number;
    attention: number;
    recommendations: number;
    high: number;
    medium: number;
    low: number;
  };
  counts?: Record<string, number>;
  path?: string;
};

export async function fetchDashboardWorkQueue(limit = 12): Promise<DashboardWorkQueue> {
  const { data } = await api.get("/api/marketing/dashboard/work-queue", { params: { limit } });
  return data as DashboardWorkQueue;
}

export type CrmSummary = {
  ok: boolean;
  total: number;
  status_counts: Record<string, number>;
  recent: Array<{ id: string; name: string; status: string; email: string; created_at: string }>;
  source: string;
  message: string;
};

export type MarketingJob = {
  id: string;
  type: string;
  status: string;
  payload: Record<string, unknown>;
  result?: unknown;
  error?: string | null;
  created_at: string;
  updated_at: string;
};

export type AgentsStatus = {
  ai: {
    ok: boolean;
    enabled: boolean;
    url: string;
    mode?: string;
    enrichment?: string;
  };
  crm: { ok: boolean; enabled: boolean; url: string; write_enabled?: boolean };
  jobs: {
    enabled: boolean;
    redis: boolean;
    backend: string;
    worker?: {
      enabled: boolean;
      interval_seconds: number;
      heartbeat?: { ok?: boolean; last_tick_at?: string | null; processed?: number };
    };
  };
  agents: string[];
  catalog?: Array<{ id: string; label: string; job_type: string; llm: boolean }>;
  enrichment?: {
    mode?: string;
    via?: string;
    fallback?: string;
    note?: string;
  };
  oauth?: {
    live_ready_count?: number;
    total_providers?: number;
    sandbox_allowed?: boolean;
    live_ready?: string[];
  };
};

export async function fetchCrmSummary(): Promise<CrmSummary> {
  const { data } = await api.get("/api/marketing/crm/summary");
  return data as CrmSummary;
}

export type CrmWritebackStatus = {
  ok: boolean;
  read_enabled: boolean;
  write_enabled: boolean;
  mode: string;
  allowed_operations: string[];
  message?: string;
  constraints?: Record<string, boolean>;
};

export type CrmWritebackTaskResult = {
  ok: boolean;
  mode: string;
  wrote?: boolean;
  write_enabled?: boolean;
  task_id?: string;
  message?: string;
  preview?: Record<string, unknown>;
};

export type CrmWritebackLeadResult = {
  ok: boolean;
  mode: string;
  wrote?: boolean;
  write_enabled?: boolean;
  lead_id?: string;
  message?: string;
  preview?: Record<string, unknown>;
};

export async function fetchCrmWritebackStatus(): Promise<CrmWritebackStatus> {
  const { data } = await api.get("/api/marketing/crm/writeback/status");
  return data as CrmWritebackStatus;
}

export async function createCrmWritebackTask(input: {
  title?: string;
  description?: string;
  priority?: string;
  lead_id?: string;
  recommendation_id?: string;
  dry_run?: boolean;
}): Promise<CrmWritebackTaskResult> {
  const { data } = await api.post("/api/marketing/crm/writeback/task", input);
  return data as CrmWritebackTaskResult;
}

export async function createCrmWritebackLead(input: {
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  notes?: string;
  campaign_id?: string;
  dry_run?: boolean;
}): Promise<CrmWritebackLeadResult> {
  const { data } = await api.post("/api/marketing/crm/writeback/lead", input);
  return data as CrmWritebackLeadResult;
}

export async function fetchAgentsStatus(): Promise<AgentsStatus> {
  const { data } = await api.get("/api/marketing/agents/status");
  return data as AgentsStatus;
}

export async function fetchJobs(): Promise<MarketingJob[]> {
  const { data } = await api.get("/api/marketing/jobs");
  return (data?.items || []) as MarketingJob[];
}

export async function enqueueJob(input: {
  type: string;
  payload?: Record<string, unknown>;
  run_now?: boolean;
}): Promise<MarketingJob> {
  const { data } = await api.post("/api/marketing/jobs", {
    type: input.type,
    payload: input.payload || {},
    run_now: input.run_now ?? true,
  });
  return data as MarketingJob;
}

export async function tickJobsWorker(): Promise<{
  claimed: number;
  completed: number;
  failed: number;
  jobs: Array<{ id?: string; status?: string; type?: string }>;
}> {
  const { data } = await api.post("/api/marketing/jobs/worker/tick");
  return data as {
    claimed: number;
    completed: number;
    failed: number;
    jobs: Array<{ id?: string; status?: string; type?: string }>;
  };
}

export async function fetchMarketingHealth(): Promise<{
  ok: boolean;
  modules: string[];
  version?: string;
  release?: string;
}> {
  try {
    const { data } = await api.get("/api/marketing/health");
    return data as { ok: boolean; modules: string[]; version?: string; release?: string };
  } catch {
    return { ok: false, modules: [] };
  }
}

export type MarketingRelease = {
  product: string;
  version: string;
  release: string;
  status: string;
  capabilities: string[];
  constraints: {
    crm_frontend_readonly: boolean;
    crm_apis_readonly: boolean;
    persistence: string;
  };
};

export async function fetchMarketingRelease(): Promise<MarketingRelease | null> {
  try {
    const { data } = await api.get("/api/marketing/release");
    return data as MarketingRelease;
  } catch {
    return null;
  }
}

export async function fetchKeywords(): Promise<KeywordItem[]> {
  const { data } = await api.get("/api/marketing/seo/keywords");
  return (data?.items || []) as KeywordItem[];
}

export async function deleteKeyword(keywordId: string): Promise<{ ok: boolean; id: string }> {
  const { data } = await api.delete(`/api/marketing/seo/keywords/${keywordId}`);
  return data as { ok: boolean; id: string };
}

export async function deleteKeywordsBulk(input: {
  scope?: "all" | "live" | "ai" | "expanded";
  ids?: string[];
}): Promise<{ ok: boolean; deleted: number; ids?: string[]; scope?: string }> {
  const { data } = await api.post("/api/marketing/seo/keywords/delete", {
    scope: input.scope || "all",
    ids: input.ids,
  });
  return data as { ok: boolean; deleted: number; ids?: string[]; scope?: string };
}

export async function researchKeywords(seed: string, opts?: { include_ai?: boolean }): Promise<{
  items: KeywordItem[];
  live_keywords?: KeywordItem[];
  ai_ideas?: KeywordItem[];
  expanded_keywords?: KeywordItem[];
  clusters?: KeywordCluster[];
  opportunities?: KeywordOpportunity[];
  sources?: Array<{ id: string; mode?: string; live?: boolean; count?: number }>;
  totals?: Record<string, number>;
  ai?: { mode?: string; source?: string; disclaimer?: string; count?: number };
  mode?: string;
  gsc_mode?: string;
  disclaimer?: string;
  synced_at?: string;
}> {
  const { data } = await api.post(
    "/api/marketing/seo/keywords/research",
    {
      seed,
      locale: "en-US",
      include_ai: opts?.include_ai ?? true,
    },
    { timeout: 120000 },
  );
  return {
    items: (data?.items || []) as KeywordItem[],
    live_keywords: (data?.live_keywords || []) as KeywordItem[],
    ai_ideas: (data?.ai_ideas || []) as KeywordItem[],
    expanded_keywords: (data?.expanded_keywords || []) as KeywordItem[],
    clusters: (data?.clusters || []) as KeywordCluster[],
    opportunities: (data?.opportunities || []) as KeywordOpportunity[],
    sources: data?.sources || [],
    totals: data?.totals || {},
    ai: data?.ai,
    mode: data?.mode,
    gsc_mode: data?.gsc_mode,
    disclaimer: data?.disclaimer,
    synced_at: data?.synced_at,
  };
}

export async function fetchKeywordClusters(): Promise<{
  items: KeywordCluster[];
  cluster_count: number;
  keyword_count: number;
  disclaimer?: string;
}> {
  const { data } = await api.get("/api/marketing/seo/keywords/clusters");
  return {
    items: (data?.items || []) as KeywordCluster[],
    cluster_count: Number(data?.cluster_count || 0),
    keyword_count: Number(data?.keyword_count || 0),
    disclaimer: data?.disclaimer,
  };
}

export async function clusterKeywords(): Promise<{
  ok: boolean;
  clustered: number;
  cluster_count: number;
  clusters: KeywordCluster[];
  mode?: string;
  disclaimer?: string;
}> {
  const { data } = await api.post("/api/marketing/seo/keywords/cluster");
  return data as {
    ok: boolean;
    clustered: number;
    cluster_count: number;
    clusters: KeywordCluster[];
    mode?: string;
    disclaimer?: string;
  };
}

export async function fetchSeoAudits(): Promise<SeoAudit[]> {
  const { data } = await api.get("/api/marketing/seo/audits");
  return (data?.items || []) as SeoAudit[];
}

export async function createSeoAudit(url: string, keyword: string): Promise<SeoAudit> {
  const { data } = await api.post("/api/marketing/seo/audits", { url, keyword });
  return data as SeoAudit;
}

export async function generateSeoMeta(url: string, keyword: string): Promise<SeoMetaDraft> {
  const { data } = await api.post("/api/marketing/seo/meta", { url, keyword });
  return data as SeoMetaDraft;
}

export type GscStatus = {
  provider: string;
  connected: boolean;
  status: string;
  mode?: string;
  site_url?: string;
  last_sync_at?: string | null;
  last_sync_mode?: string;
  last_sync_reason?: string;
  last_totals?: { clicks?: number; impressions?: number; queries?: number; ctr?: number };
  sandbox_available?: boolean;
  service_account_ready?: boolean;
  live_capable?: boolean;
  disclaimer?: string;
};

export type GscQueryRow = {
  id: string;
  site_url: string;
  day: string;
  query: string;
  page?: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  meta?: Record<string, unknown>;
};

export type GscSyncResult = {
  ok: boolean;
  mode?: string;
  reason?: string;
  site_url?: string;
  totals?: { clicks?: number; impressions?: number; queries?: number; ctr?: number };
  rows?: GscQueryRow[];
  disclaimer?: string;
  synced_at?: string;
};

export async function fetchGscStatus(): Promise<GscStatus> {
  const { data } = await api.get("/api/marketing/seo/gsc/status");
  return data as GscStatus;
}

export async function fetchGscQueries(limit = 50): Promise<GscQueryRow[]> {
  const { data } = await api.get("/api/marketing/seo/gsc/queries", { params: { limit } });
  return (data?.items || []) as GscQueryRow[];
}

export async function setGscSite(siteUrl: string): Promise<{ ok: boolean; site_url: string }> {
  const { data } = await api.patch("/api/marketing/seo/gsc/site", { site_url: siteUrl });
  return data as { ok: boolean; site_url: string };
}

export async function syncGsc(opts?: { days?: number; limit?: number }): Promise<GscSyncResult> {
  const { data } = await api.post("/api/marketing/seo/gsc/sync", {
    days: opts?.days ?? 7,
    limit: opts?.limit ?? 25,
  });
  return data as GscSyncResult;
}

export type SeoHubSource = {
  id: string;
  label?: string;
  ok?: boolean;
  mode?: string;
  live?: boolean;
  reason?: string;
  help?: string;
  synced_at?: string;
  disclaimer?: string;
};

export type SeoHubStatus = {
  site_url?: string;
  gsc_site_url?: string;
  ga4_property_id?: string;
  sources: SeoHubSource[];
  last_sync_at?: string | null;
  pagespeed_configured?: boolean;
};

export type SeoHubOverview = {
  status: SeoHubStatus;
  kpis: {
    gsc?: { clicks?: number; impressions?: number; queries?: number; pages?: number };
    ga4?: Record<string, number>;
    technical?: Record<string, number | boolean>;
    pagespeed?: { urls?: number; avg_performance?: number | null };
  };
  top_queries?: Array<Record<string, unknown>>;
  top_pages?: Array<Record<string, unknown>>;
  top_landings?: Array<Record<string, unknown>>;
  recommendations?: SeoHubRecommendation[];
  competitors?: Array<Record<string, unknown>>;
  last_sync_at?: string | null;
};

export type SeoHubRecommendation = {
  id: string;
  kind?: string;
  priority?: string;
  title: string;
  summary?: string;
  ai_summary?: string;
  evidence?: string[];
  actions?: string[];
  sources?: string[];
  deep_link?: string;
  created_at?: string;
};

export async function fetchSeoHubStatus(): Promise<SeoHubStatus> {
  const { data } = await api.get("/api/marketing/seo/hub/status");
  return data as SeoHubStatus;
}

export async function fetchSeoHubOverview(): Promise<SeoHubOverview> {
  const { data } = await api.get("/api/marketing/seo/hub/overview");
  return data as SeoHubOverview;
}

export async function syncSeoHub(opts?: {
  days?: number;
  site_url?: string;
  max_pages?: number;
  run_pagespeed?: boolean;
  run_trends?: boolean;
}): Promise<{ ok: boolean; synced_at?: string; overview?: SeoHubOverview; sources?: SeoHubSource[] }> {
  const { data } = await api.post(
    "/api/marketing/seo/hub/sync",
    {
      days: opts?.days ?? 28,
      site_url: opts?.site_url,
      max_pages: opts?.max_pages,
      run_pagespeed: opts?.run_pagespeed ?? true,
      run_trends: opts?.run_trends ?? true,
    },
    // Crawl + PageSpeed often exceed the default 30s client/gateway window.
    { timeout: 180000 },
  );
  return data as { ok: boolean; synced_at?: string; overview?: SeoHubOverview; sources?: SeoHubSource[] };
}

export async function fetchSeoHubKeywords(): Promise<{ items: Array<Record<string, unknown>>; synced_at?: string }> {
  const { data } = await api.get("/api/marketing/seo/hub/keywords");
  return data as { items: Array<Record<string, unknown>>; synced_at?: string };
}

export async function fetchSeoHubPages(): Promise<{ items: Array<Record<string, unknown>>; synced_at?: string }> {
  const { data } = await api.get("/api/marketing/seo/hub/pages");
  return data as { items: Array<Record<string, unknown>>; synced_at?: string };
}

export async function fetchSeoHubTechnical(): Promise<{
  crawl?: Record<string, unknown>;
  pagespeed?: Array<Record<string, unknown>>;
  synced_at?: string;
}> {
  const { data } = await api.get("/api/marketing/seo/hub/technical");
  return data as { crawl?: Record<string, unknown>; pagespeed?: Array<Record<string, unknown>>; synced_at?: string };
}

export async function fetchSeoHubCompetitors(): Promise<{ items: Array<Record<string, unknown>>; synced_at?: string }> {
  const { data } = await api.get("/api/marketing/seo/hub/competitors");
  return data as { items: Array<Record<string, unknown>>; synced_at?: string };
}

export async function fetchSeoHubInsights(): Promise<{
  recommendations?: SeoHubRecommendation[];
  synced_at?: string;
  kpis?: SeoHubOverview["kpis"];
}> {
  const { data } = await api.get("/api/marketing/seo/hub/insights");
  return data as {
    recommendations?: SeoHubRecommendation[];
    synced_at?: string;
    kpis?: SeoHubOverview["kpis"];
  };
}

export async function fetchContentDrafts(): Promise<ContentDraft[]> {
  const { data } = await api.get("/api/marketing/content/drafts");
  return (data?.items || []) as ContentDraft[];
}

export async function createContentDraft(input: {
  title: string;
  channel: string;
  brief: string;
  tone?: string;
  use_brand_voice?: boolean;
  brand_voice?: string;
  body?: string;
  meta?: Record<string, unknown>;
}): Promise<ContentDraft> {
  const { data } = await api.post("/api/marketing/content/drafts", {
    title: input.title,
    channel: input.channel,
    brief: input.brief,
    tone: input.tone,
    use_brand_voice: input.use_brand_voice ?? true,
    brand_voice: input.brand_voice,
    body: input.body,
    meta: input.meta,
  });
  return data as ContentDraft;
}

export type ContentFactoryChannel = {
  id: string;
  label: string;
  description: string;
  brand_voices: string[];
};

export type ContentFactoryGenerateResult = {
  ok: boolean;
  channel: string;
  title: string;
  brief: string;
  brand_voice: string;
  body: string;
  source?: string;
  mode?: string;
  template?: string;
  disclaimer?: string;
  draft?: ContentDraft;
};

export async function fetchContentFactoryChannels(): Promise<{
  items: ContentFactoryChannel[];
  brand_voices: Array<{ id: string; hint: string }>;
  disclaimer?: string;
}> {
  const { data } = await api.get("/api/marketing/content/factory/channels");
  return {
    items: (data?.items || []) as ContentFactoryChannel[],
    brand_voices: (data?.brand_voices || []) as Array<{ id: string; hint: string }>,
    disclaimer: data?.disclaimer,
  };
}

export async function generateContentFactory(input: {
  title: string;
  channel: string;
  brief?: string;
  brand_voice?: string;
  use_brand_voice?: boolean;
  save?: boolean;
}): Promise<ContentFactoryGenerateResult> {
  const { data } = await api.post("/api/marketing/content/factory/generate", {
    title: input.title,
    channel: input.channel,
    brief: input.brief || "",
    brand_voice: input.brand_voice,
    use_brand_voice: input.use_brand_voice ?? true,
    save: Boolean(input.save),
  });
  return data as ContentFactoryGenerateResult;
}

export type ContentStudioGoal = "traffic" | "leads" | "sales" | "awareness";

export type ContentStudioPlatform = {
  id: string;
  label: string;
  description: string;
  brand_voices: string[];
  supports_hashtags?: boolean;
  supports_seo?: boolean;
  limits?: { words_min?: number; words_max?: number; chars_max?: number | null };
};

export type ContentStudioVariation = {
  index: number;
  label: string;
  angle: string;
  content: string;
  word_count: number;
  char_count: number;
  source_content?: string;
  edited?: boolean;
};

export type ContentStudioGenerateResult = {
  ok: boolean;
  platform: string;
  platform_label: string;
  goal: ContentStudioGoal;
  goal_label: string;
  title: string;
  brief: string;
  audience: string;
  tone: string;
  cta: string;
  variations: ContentStudioVariation[];
  hashtags: string[];
  seo_keywords: string[];
  limits?: ContentStudioPlatform["limits"];
  source?: string;
  mode?: string;
  disclaimer?: string;
  draft?: ContentDraft;
};

export async function fetchContentStudioPlatforms(): Promise<{
  platforms: ContentStudioPlatform[];
  goals: Array<{ id: ContentStudioGoal; label: string }>;
  brand_voices: Array<{ id: string; hint: string }>;
  variation_count: number;
  disclaimer?: string;
}> {
  const { data } = await api.get("/api/marketing/content/studio/platforms");
  return {
    platforms: (data?.platforms || []) as ContentStudioPlatform[],
    goals: (data?.goals || []) as Array<{ id: ContentStudioGoal; label: string }>,
    brand_voices: (data?.brand_voices || []) as Array<{ id: string; hint: string }>,
    variation_count: Number(data?.variation_count || 3),
    disclaimer: data?.disclaimer,
  };
}

export async function generateContentStudio(input: {
  topic: string;
  product?: string;
  url?: string;
  goal?: ContentStudioGoal;
  audience?: string;
  platform: string;
  tone?: string;
  cta?: string;
  use_brand_voice?: boolean;
  brand_voice?: string;
  save?: boolean;
  variation_index?: number;
}): Promise<ContentStudioGenerateResult> {
  const { data } = await api.post(
    "/api/marketing/content/studio/generate",
    {
      topic: input.topic,
      product: input.product || "",
      url: input.url || "",
      goal: input.goal || "leads",
      audience: input.audience || "",
      platform: input.platform,
      tone: input.tone,
      cta: input.cta || "",
      use_brand_voice: input.use_brand_voice ?? true,
      brand_voice: input.brand_voice,
      save: Boolean(input.save),
      variation_index: input.variation_index,
    },
    // AI enrichment can exceed the default 30s axios timeout on slower providers.
    { timeout: 90_000 },
  );
  return data as ContentStudioGenerateResult;
}

export async function updateContentDraft(
  draftId: string,
  input: {
    status?: string;
    title?: string;
    body?: string;
    brief?: string;
    tone?: string;
    meta?: Record<string, unknown>;
  },
): Promise<ContentDraft> {
  const { data } = await api.patch(`/api/marketing/content/drafts/${draftId}`, input);
  return data as ContentDraft;
}

export async function deleteContentDraft(draftId: string): Promise<{ ok: boolean; id: string }> {
  const { data } = await api.delete(`/api/marketing/content/drafts/${draftId}`);
  return data as { ok: boolean; id: string };
}

/** Publish a Content Studio draft to a channel without creating a Social Media queue item. */
export async function publishContentDraftSocial(
  draftId: string,
  input?: {
    platform?: string;
    mode?: string;
    dry_run?: boolean;
  },
): Promise<{
  ok: boolean;
  mode?: string;
  platform?: string;
  draft?: ContentDraft;
  message?: string;
  reason?: string;
  vendor?: Record<string, unknown>;
}> {
  const { data } = await api.post(`/api/marketing/content/drafts/${draftId}/publish-social`, {
    platform: input?.platform || undefined,
    mode: input?.mode || "vendor",
    dry_run: input?.dry_run ?? false,
  });
  return data as {
    ok: boolean;
    mode?: string;
    platform?: string;
    draft?: ContentDraft;
    message?: string;
    reason?: string;
    vendor?: Record<string, unknown>;
  };
}

export async function submitContentDraftReview(
  draftId: string,
  input?: { note?: string; publish_at?: string | null },
): Promise<{ ok: boolean; already_pending?: boolean; item: ContentDraft }> {
  const { data } = await api.post(`/api/marketing/content/drafts/${draftId}/submit-review`, {
    note: input?.note || "",
    publish_at: input?.publish_at || null,
  });
  return data as { ok: boolean; already_pending?: boolean; item: ContentDraft };
}

export async function promoteContentDraftToSocial(
  draftId: string,
  input?: {
    platform?: string;
    platforms?: string[];
    scheduled_at?: string | null;
    status?: "scheduled" | "needs_approval" | "draft" | "published";
    caption?: string;
  },
): Promise<{
  ok: boolean;
  draft: ContentDraft;
  social: SocialPost;
  socials?: SocialPost[];
  platforms?: string[];
  skipped?: Array<{ platform: string; reason: string }>;
}> {
  const { data } = await api.post(`/api/marketing/content/drafts/${draftId}/promote-social`, {
    platform: input?.platforms?.length ? undefined : input?.platform || undefined,
    platforms: input?.platforms?.length ? input.platforms : undefined,
    scheduled_at: input?.scheduled_at || null,
    status: input?.status || "scheduled",
    caption: input?.caption || null,
  });
  return data as {
    ok: boolean;
    draft: ContentDraft;
    social: SocialPost;
    socials?: SocialPost[];
    platforms?: string[];
    skipped?: Array<{ platform: string; reason: string }>;
  };
}

export async function publishContentDraftToWebsite(
  draftId: string,
  input?: { status?: "Published" | "Draft"; run_async?: boolean },
): Promise<{
  ok: boolean;
  url?: string;
  slug?: string;
  blog_id?: string;
  draft?: ContentDraft;
  queued?: boolean;
  job?: Record<string, unknown>;
  mode?: string;
}> {
  const { data } = await api.post(
    `/api/marketing/content/drafts/${draftId}/publish-website`,
    {
      status: input?.status || "Published",
      run_async: input?.run_async ?? false,
    },
    { timeout: 90000 },
  );
  return data as {
    ok: boolean;
    url?: string;
    slug?: string;
    blog_id?: string;
    draft?: ContentDraft;
    queued?: boolean;
    job?: Record<string, unknown>;
    mode?: string;
  };
}

export type ContentCalendarItem = {
  id: string;
  kind: "draft" | "social" | string;
  title: string;
  status?: string;
  when?: string | null;
  path: string;
  snippet?: string;
  channel?: string;
  platform?: string;
};

export type ContentCalendar = {
  ok: boolean;
  days: Record<string, ContentCalendarItem[]>;
  items: ContentCalendarItem[];
  counts: { total: number; draft: number; social: number; in_review: number; days: number };
  filters?: {
    kind?: string;
    platform?: string | null;
    date_from?: string | null;
    date_to?: string | null;
  };
};

export async function fetchContentCalendar(opts?: {
  kind?: "all" | "draft" | "social";
  platform?: string;
  date_from?: string;
  date_to?: string;
}): Promise<ContentCalendar> {
  const { data } = await api.get("/api/marketing/content/calendar", {
    params: {
      ...(opts?.kind && opts.kind !== "all" ? { kind: opts.kind } : {}),
      ...(opts?.platform ? { platform: opts.platform } : {}),
      ...(opts?.date_from ? { date_from: opts.date_from } : {}),
      ...(opts?.date_to ? { date_to: opts.date_to } : {}),
    },
  });
  return data as ContentCalendar;
}

export type ContentCalendarWeekDay = {
  date: string;
  weekday: string;
  count: number;
  items: ContentCalendarItem[];
};

export type ContentCalendarWeek = {
  ok: boolean;
  week_start: string;
  week_end: string;
  days: Record<string, ContentCalendarItem[]>;
  ordered_days: ContentCalendarWeekDay[];
  items: ContentCalendarItem[];
  counts: {
    total: number;
    draft: number;
    social: number;
    in_review: number;
    days: number;
    week_days?: number;
    populated_days?: number;
  };
  filters?: {
    kind?: string;
    platform?: string | null;
    week_start?: string;
  };
  paths?: Record<string, string>;
};

export async function fetchContentCalendarWeek(opts?: {
  week_start?: string;
  kind?: "all" | "draft" | "social";
  platform?: string;
}): Promise<ContentCalendarWeek> {
  const { data } = await api.get("/api/marketing/content/calendar/week", {
    params: {
      ...(opts?.week_start ? { week_start: opts.week_start } : {}),
      ...(opts?.kind && opts.kind !== "all" ? { kind: opts.kind } : {}),
      ...(opts?.platform ? { platform: opts.platform } : {}),
    },
  });
  return data as ContentCalendarWeek;
}

export type CalendarPublishCheck = {
  id: string;
  label: string;
  ok: boolean;
  severity: string;
  message: string;
};

export type CalendarPublishDay = {
  ok: boolean;
  date: string;
  ready: boolean;
  publish_ready: boolean;
  items?: ContentCalendarItem[];
  item_count: number;
  summary: { pass: number; fail: number; warn: number; total: number };
  checks: CalendarPublishCheck[];
  mode?: string;
  disclaimer?: string;
};

export async function fetchCalendarPublishChecks(): Promise<{
  items: Array<{ id: string; label: string; severity: string }>;
  mode?: string;
  disclaimer?: string;
}> {
  const { data } = await api.get("/api/marketing/content/calendar/publish-checks");
  return data as {
    items: Array<{ id: string; label: string; severity: string }>;
    mode?: string;
    disclaimer?: string;
  };
}

export async function fetchCalendarPublishDay(opts?: {
  date?: string;
  kind?: "all" | "draft" | "social";
  platform?: string;
}): Promise<CalendarPublishDay> {
  const { data } = await api.get("/api/marketing/content/calendar/publish-day", {
    params: {
      ...(opts?.date ? { date: opts.date } : {}),
      ...(opts?.kind && opts.kind !== "all" ? { kind: opts.kind } : {}),
      ...(opts?.platform ? { platform: opts.platform } : {}),
    },
  });
  return data as CalendarPublishDay;
}

export async function fetchMarketingCampaigns(): Promise<MarketingCampaign[]> {
  const { data } = await api.get("/api/marketing/campaigns");
  return (data?.items || []) as MarketingCampaign[];
}

export async function fetchMarketingCampaign(campaignId: string): Promise<MarketingCampaign> {
  const { data } = await api.get(`/api/marketing/campaigns/${campaignId}`);
  return data as MarketingCampaign;
}

export async function fetchCampaignHub(campaignId: string): Promise<CampaignHubPayload> {
  const { data } = await api.get(`/api/marketing/campaigns/${campaignId}/hub`);
  return data as CampaignHubPayload;
}

export async function createMarketingCampaign(input: {
  name: string;
  channels: string[];
  budget: number;
  status: string;
  objective?: string;
  audience?: string;
  starts_at?: string | null;
  ends_at?: string | null;
  target_leads?: number | null;
  primary_kpi?: string;
}): Promise<MarketingCampaign> {
  const { data } = await api.post("/api/marketing/campaigns", input);
  return data as MarketingCampaign;
}

export async function updateMarketingCampaign(
  campaignId: string,
  input: {
    name?: string;
    channels?: string[];
    budget?: number;
    status?: string;
    objective?: string;
    audience?: string;
    starts_at?: string | null;
    ends_at?: string | null;
    target_leads?: number | null;
    primary_kpi?: string;
    utm?: Record<string, string>;
  },
): Promise<MarketingCampaign> {
  const { data } = await api.patch(`/api/marketing/campaigns/${campaignId}`, input);
  return data as MarketingCampaign;
}

export async function cloneMarketingCampaign(
  campaignId: string,
  name?: string,
): Promise<MarketingCampaign> {
  const { data } = await api.post(`/api/marketing/campaigns/${campaignId}/clone`, {
    name: name || undefined,
  });
  return data as MarketingCampaign;
}

export async function linkCampaignAsset(
  campaignId: string,
  input: { kind: "social" | "draft" | "ad"; asset_id: string },
): Promise<Record<string, unknown>> {
  const { data } = await api.post(`/api/marketing/campaigns/${campaignId}/link`, input);
  return data as Record<string, unknown>;
}

export async function unlinkCampaignAsset(
  campaignId: string,
  input: { kind: "social" | "draft" | "ad"; asset_id: string },
): Promise<Record<string, unknown>> {
  const { data } = await api.post(`/api/marketing/campaigns/${campaignId}/unlink`, input);
  return data as Record<string, unknown>;
}

export async function generateCampaignBrief(campaignId: string): Promise<{
  ok: boolean;
  brief: Record<string, unknown>;
  campaign: MarketingCampaign;
}> {
  const { data } = await api.post(`/api/marketing/campaigns/${campaignId}/brief`);
  return data as { ok: boolean; brief: Record<string, unknown>; campaign: MarketingCampaign };
}

export async function buildCampaignUtm(
  campaignId: string,
  input: {
    base_url?: string;
    source?: string;
    medium?: string;
    content?: string;
    persist?: boolean;
  },
): Promise<{ ok: boolean; params: Record<string, string>; url: string; persisted?: boolean }> {
  const { data } = await api.post(`/api/marketing/campaigns/${campaignId}/utm`, input);
  return data as { ok: boolean; params: Record<string, string>; url: string; persisted?: boolean };
}

export async function fetchCampaignsCalendar(campaignId?: string): Promise<{
  ok: boolean;
  days: Record<string, Array<Record<string, unknown>>>;
  counts: { days: number; items: number; unscheduled: number };
}> {
  const { data } = await api.get("/api/marketing/campaigns/calendar", {
    params: campaignId ? { campaign_id: campaignId } : {},
  });
  return data as {
    ok: boolean;
    days: Record<string, Array<Record<string, unknown>>>;
    counts: { days: number; items: number; unscheduled: number };
  };
}

export async function fetchBiReports(): Promise<BiReport[]> {
  const { data } = await api.get("/api/marketing/bi/reports");
  return (data?.items || []) as BiReport[];
}

export async function generateBiReport(input: {
  company: string;
  industry: string;
  report_type: string;
  website?: string;
  use_ai?: boolean;
  fetch_website?: boolean;
}): Promise<BiReport> {
  const { data } = await api.post("/api/marketing/bi/generate", {
    company: input.company,
    industry: input.industry,
    report_type: input.report_type,
    website: input.website,
    use_ai: input.use_ai ?? true,
    fetch_website: input.fetch_website ?? true,
  });
  return data as BiReport;
}

export async function deleteBiReport(reportId: string): Promise<{ ok: boolean; id: string }> {
  const { data } = await api.delete(`/api/marketing/bi/reports/${reportId}`);
  return data as { ok: boolean; id: string };
}

export async function exportBiReport(
  reportId: string,
  format: "markdown" | "json" = "markdown",
): Promise<{ ok: boolean; format: string; filename: string; content: string | BiReport }> {
  const { data } = await api.get(`/api/marketing/bi/reports/${reportId}/export`, {
    params: { format },
  });
  return data as { ok: boolean; format: string; filename: string; content: string | BiReport };
}

/** Marketing Intelligence Platform (normalized BI) */
export type BiIntelDashboard = {
  ok: boolean;
  scores: Record<string, number>;
  metrics: Array<Record<string, unknown>>;
  opportunities: Array<Record<string, unknown>>;
  syncs: Array<Record<string, unknown>>;
  connectors: Array<Record<string, unknown>>;
  executive_reports: Array<Record<string, unknown>>;
  risks: Array<Record<string, unknown>>;
};

export async function fetchBiIntelDashboard(): Promise<BiIntelDashboard> {
  const { data } = await api.get("/api/marketing/bi/intel/dashboard");
  return data as BiIntelDashboard;
}

export async function fetchBiIntelConnectors(): Promise<{ ok: boolean; items: Array<Record<string, unknown>> }> {
  const { data } = await api.get("/api/marketing/bi/intel/connectors");
  return data as { ok: boolean; items: Array<Record<string, unknown>> };
}

export async function syncBiIntelConnector(
  provider: string,
  opts?: { days?: number; run_pipeline?: boolean },
): Promise<Record<string, unknown>> {
  const { data } = await api.post("/api/marketing/bi/intel/sync", {
    provider,
    days: opts?.days ?? 28,
    run_pipeline: opts?.run_pipeline ?? true,
  });
  return data as Record<string, unknown>;
}

export async function runBiIntelPipeline(opts?: {
  providers?: string[];
  run_ai?: boolean;
}): Promise<Record<string, unknown>> {
  const { data } = await api.post("/api/marketing/bi/intel/pipeline", {
    providers: opts?.providers,
    run_ai: opts?.run_ai ?? false,
  });
  return data as Record<string, unknown>;
}

export async function fetchBiIntelOpportunities(): Promise<{
  ok: boolean;
  items: Array<Record<string, unknown>>;
}> {
  const { data } = await api.get("/api/marketing/bi/intel/opportunities");
  return data as { ok: boolean; items: Array<Record<string, unknown>> };
}

export async function fetchBiIntelFacts(modelType?: string): Promise<{
  ok: boolean;
  items: Array<Record<string, unknown>>;
}> {
  const { data } = await api.get("/api/marketing/bi/intel/facts", {
    params: modelType ? { model_type: modelType } : undefined,
  });
  return data as { ok: boolean; items: Array<Record<string, unknown>> };
}

export async function fetchBiIntelMetrics(): Promise<{ ok: boolean; items: Array<Record<string, unknown>> }> {
  const { data } = await api.get("/api/marketing/bi/intel/metrics");
  return data as { ok: boolean; items: Array<Record<string, unknown>> };
}

export async function generateBiIntelExecutive(): Promise<Record<string, unknown>> {
  const { data } = await api.post("/api/marketing/bi/intel/reports/executive");
  return data as Record<string, unknown>;
}

export async function fetchBiIntelChat(sessionId = "default"): Promise<{
  ok: boolean;
  items: Array<{ id: string; role: string; content: string; citations?: string[] }>;
}> {
  const { data } = await api.get("/api/marketing/bi/intel/chat", { params: { session_id: sessionId } });
  return data as {
    ok: boolean;
    items: Array<{ id: string; role: string; content: string; citations?: string[] }>;
  };
}

export async function postBiIntelChat(
  question: string,
  sessionId = "default",
): Promise<Record<string, unknown>> {
  const { data } = await api.post("/api/marketing/bi/intel/chat", {
    question,
    session_id: sessionId,
  });
  return data as Record<string, unknown>;
}

export async function exportBiIntelDashboard(): Promise<{ ok: boolean; content: BiIntelDashboard }> {
  const { data } = await api.get("/api/marketing/bi/intel/export");
  return data as { ok: boolean; content: BiIntelDashboard };
}

export async function fetchCompetitors(): Promise<Competitor[]> {
  const { data } = await api.get("/api/marketing/competitors");
  return (data?.items || []) as Competitor[];
}

export async function createCompetitor(input: {
  name: string;
  domain: string;
  notes: string;
  pricing_tier: string;
}): Promise<Competitor> {
  const { data } = await api.post("/api/marketing/competitors", input);
  return data as Competitor;
}

export async function patchCompetitor(
  competitorId: string,
  patch: { name?: string; notes?: string; pricing_tier?: string; monitoring_enabled?: boolean },
): Promise<Competitor> {
  const { data } = await api.patch(`/api/marketing/competitors/${competitorId}`, patch);
  return data as Competitor;
}

export async function fetchCompetitorEvents(opts?: {
  competitor_id?: string;
  limit?: number;
}): Promise<CompetitorEvent[]> {
  const { data } = await api.get("/api/marketing/competitors/events", {
    params: {
      competitor_id: opts?.competitor_id || undefined,
      limit: opts?.limit || 50,
    },
  });
  return (data?.items || []) as CompetitorEvent[];
}

export async function monitorCompetitors(input?: {
  competitor_id?: string;
  force_change?: boolean;
  notify?: boolean;
  enqueue?: boolean;
}): Promise<CompetitorMonitorResult> {
  const { data } = await api.post("/api/marketing/competitors/monitor", input || {});
  return data as CompetitorMonitorResult;
}

/* ---- Competitor Intelligence Platform (Phase 1) ---- */

export type CiCompetitor = {
  id: string;
  name: string;
  domain: string;
  industry?: string;
  priority?: string;
  tags?: string[];
  scan_frequency?: string;
  status?: string;
  logo_url?: string;
  health_score?: number;
  threat_level?: string;
  momentum?: number;
  last_scanned_at?: string | null;
  ai_one_liner?: string;
  data_freshness?: string;
  crawl_status?: string;
  last_change_summary?: string;
  last_change_at?: string;
  last_change_id?: string;
  strategy_hypothesis?: string;
  meta?: Record<string, unknown>;
};

export type CiScan = {
  id: string;
  competitor_id: string;
  status: string;
  progress_steps: Array<{ id: string; label: string; status: string; detail?: string }>;
  started_at?: string | null;
  completed_at?: string | null;
  error?: string | null;
  meta?: Record<string, unknown>;
};

export type CiOpportunity = {
  id: string;
  competitor_id: string;
  title: string;
  description: string;
  priority: string;
  impact: string;
  suggested_actions: Array<{ label: string; path: string }>;
  signal_type?: string;
};

export type CiAlert = {
  id: string;
  competitor_id: string;
  alert_type: string;
  priority: string;
  title: string;
  description: string;
  is_read: boolean;
  path: string;
  created_at?: string;
};

export type CiDashboard = {
  ok: boolean;
  greeting: string;
  phase?: number;
  stats: {
    website_changes: number;
    pricing_changes: number;
    new_articles: number;
    technology_changes: number;
    new_products: number;
    opportunity_score: number;
    ai_expansion?: number;
  };
  executive_brief?: {
    headline?: string;
    narrative?: string;
    quiet_day?: boolean;
    verified_facts?: Array<Record<string, unknown>>;
    highest_threat?: Record<string, unknown>;
    highest_growth?: Record<string, unknown>;
    most_active?: Record<string, unknown>;
    summary_lines?: string[];
    suggested_actions?: Array<Record<string, unknown>>;
    focus_actions?: Array<Record<string, unknown>>;
    watchlist?: Array<Record<string, unknown>>;
    stats?: Record<string, unknown>;
  };
  ai_summary: string[];
  alerts: CiAlert[];
  opportunities: CiOpportunity[];
  competitors_count: number;
  disclaimer?: string;
  mode?: string;
};

export async function fetchCiDashboard(): Promise<CiDashboard> {
  const { data } = await api.get("/api/marketing/competitor-intelligence/dashboard");
  return data as CiDashboard;
}

export async function fetchCiCompetitors(): Promise<CiCompetitor[]> {
  const { data } = await api.get("/api/marketing/competitor-intelligence/competitors");
  return (data?.items || []) as CiCompetitor[];
}

export async function createCiCompetitor(input: {
  name: string;
  domain: string;
  industry?: string;
  priority?: string;
  tags?: string[];
  scan_frequency?: string;
  run_scan?: boolean;
}): Promise<{ ok: boolean; competitor: CiCompetitor; scan?: CiScan & { ok?: boolean; scan?: CiScan } }> {
  const { data } = await api.post("/api/marketing/competitor-intelligence/competitors", input);
  return data as { ok: boolean; competitor: CiCompetitor; scan?: CiScan & { ok?: boolean; scan?: CiScan } };
}

export async function fetchCiCompetitorDetail(id: string): Promise<{
  ok: boolean;
  competitor: CiCompetitor;
  insight?: Record<string, unknown>;
  strategy?: Record<string, unknown>;
  intelligence_scores?: Record<string, number>;
  opportunities: CiOpportunity[];
  timeline: Array<Record<string, unknown>>;
  recent_changes: Array<Record<string, unknown>>;
  providers?: Record<string, unknown>;
  phase?: number;
}> {
  const { data } = await api.get(`/api/marketing/competitor-intelligence/competitors/${id}`);
  return data as {
    ok: boolean;
    competitor: CiCompetitor;
    insight?: Record<string, unknown>;
    strategy?: Record<string, unknown>;
    intelligence_scores?: Record<string, number>;
    opportunities: CiOpportunity[];
    timeline: Array<Record<string, unknown>>;
    recent_changes: Array<Record<string, unknown>>;
    providers?: Record<string, unknown>;
    phase?: number;
  };
}

export async function startCiScan(competitorId: string): Promise<{ ok: boolean; scan?: CiScan }> {
  const { data } = await api.post(`/api/marketing/competitor-intelligence/competitors/${competitorId}/scan`);
  return data as { ok: boolean; scan?: CiScan };
}

export async function fetchCiScan(scanId: string): Promise<{ ok: boolean; scan: CiScan }> {
  const { data } = await api.get(`/api/marketing/competitor-intelligence/scans/${scanId}`);
  return data as { ok: boolean; scan: CiScan };
}

export async function fetchCiOpportunities(competitorId?: string): Promise<CiOpportunity[]> {
  const { data } = await api.get("/api/marketing/competitor-intelligence/opportunities", {
    params: { competitor_id: competitorId || undefined },
  });
  return (data?.items || []) as CiOpportunity[];
}

export async function fetchCiAlerts(): Promise<CiAlert[]> {
  const { data } = await api.get("/api/marketing/competitor-intelligence/alerts");
  return (data?.items || []) as CiAlert[];
}

export async function patchCiAlert(alertId: string, patch: { is_read?: boolean }): Promise<CiAlert> {
  const { data } = await api.patch(`/api/marketing/competitor-intelligence/alerts/${alertId}`, patch);
  return (data?.alert || data) as CiAlert;
}

export async function fetchCiChangeDetail(
  competitorId: string,
  changeId: string,
): Promise<Record<string, unknown>> {
  const { data } = await api.get(
    `/api/marketing/competitor-intelligence/competitors/${competitorId}/changes/${changeId}`,
  );
  return data as Record<string, unknown>;
}

export async function compareCiCompetitors(competitorIds: string[]): Promise<Record<string, unknown>> {
  const { data } = await api.post("/api/marketing/competitor-intelligence/compare", {
    competitor_ids: competitorIds,
  });
  return data as Record<string, unknown>;
}

export async function generateCiReport(kind: string = "weekly"): Promise<Record<string, unknown>> {
  const { data } = await api.post("/api/marketing/competitor-intelligence/reports/generate", { kind });
  return data as Record<string, unknown>;
}

export async function fetchCiProviderDashboard(
  provider: string,
  competitorId?: string,
): Promise<Record<string, unknown>> {
  const { data } = await api.get(`/api/marketing/competitor-intelligence/providers/${provider}`, {
    params: { competitor_id: competitorId || undefined },
  });
  return data as Record<string, unknown>;
}

export async function fetchCiStrategy(competitorId: string): Promise<Record<string, unknown>> {
  const { data } = await api.get(`/api/marketing/competitor-intelligence/competitors/${competitorId}/strategy`);
  return data as Record<string, unknown>;
}

export async function fetchSocialPosts(): Promise<SocialPost[]> {
  const { data } = await api.get("/api/marketing/social/posts");
  return (data?.items || []) as SocialPost[];
}

export type SocialHashtagGroup = {
  tier: string;
  label: string;
  reach: string;
  items: string[];
};

export type SocialHashtagPlan = {
  ok: boolean;
  platform: string;
  platform_label?: string;
  limit: number;
  hashtags: string[];
  groups: SocialHashtagGroup[];
  note?: string;
  reason?: string;
};

/** Platform-specific, reach-tiered hashtag suggestions (see app/hashtags.py). */
export async function suggestSocialHashtags(input: {
  topic: string;
  platform: string;
  goal?: string;
  audience?: string;
  limit?: number;
}): Promise<SocialHashtagPlan> {
  const { data } = await api.post("/api/marketing/social/hashtags", input);
  return data as SocialHashtagPlan;
}

export async function createSocialPost(input: {
  platform: string;
  topic: string;
  caption: string;
  status: string;
  scheduled_at?: string | null;
  image_url?: string | null;
  image_urls?: string[] | null;
  video_url?: string | null;
  ig_format?: "video" | "reels" | null;
  share_to_feed?: boolean | null;
  cover_url?: string | null;
}): Promise<SocialPost> {
  const { data } = await api.post("/api/marketing/social/posts", input);
  return data as SocialPost;
}

export async function updateSocialPost(
  postId: string,
  input: {
    caption?: string;
    topic?: string | null;
    status?: string;
    scheduled_at?: string | null;
    platform?: string;
    image_url?: string | null;
    image_urls?: string[] | null;
    video_url?: string | null;
    ig_format?: "video" | "reels" | null;
    share_to_feed?: boolean | null;
    cover_url?: string | null;
  },
): Promise<SocialPost> {
  const { data } = await api.patch(`/api/marketing/social/posts/${postId}`, input);
  return data as SocialPost;
}

export async function deleteSocialPost(postId: string): Promise<{ ok: boolean; id: string }> {
  const { data } = await api.delete(`/api/marketing/social/posts/${postId}`);
  return data as { ok: boolean; id: string };
}

// --- Social Inbox (comments / replies / mentions) ---------------------------

export type SocialInboxPlatformCapability = {
  platform: string;
  label: string;
  provider: string;
  connected: boolean;
  ready: boolean;
  reason: string;
  message: string;
  comments: boolean;
  replies: boolean;
  mentions: boolean;
  messages: boolean;
  reply_write: boolean;
  notes: string[];
  token_source?: string;
  last_sync?: Record<string, unknown> | null;
};

export type SocialInboxThread = {
  id: string;
  platform: string;
  kind: string;
  external_thread_id: string;
  root_external_id: string;
  permalink: string;
  author_name: string;
  author_handle: string;
  author_avatar: string;
  subject: string;
  snippet: string;
  status: string;
  priority: string;
  assigned_to: string | null;
  message_count: number;
  unread: boolean;
  last_message_at: string | null;
  replied_at: string | null;
  tags: string[];
};

export type SocialInboxMessage = {
  id: string;
  thread_id: string;
  direction: string;
  kind: string;
  author_name: string;
  author_handle: string;
  text: string;
  permalink: string;
  status: string;
  source: string;
  external_created_at: string | null;
  created_at: string | null;
};

export type SocialInboxDraft = {
  id: string;
  thread_id: string;
  platform: string;
  source: string;
  tone: string;
  label: string;
  text: string;
  status: string;
  rationale: string;
  approved_by: string | null;
  approved_at: string | null;
  sent_at: string | null;
  vendor_reply_id: string | null;
  error: string | null;
};

export type SocialInboxThreadDetail = {
  ok: boolean;
  thread: SocialInboxThread;
  messages: SocialInboxMessage[];
  drafts: SocialInboxDraft[];
  context: {
    platform_label: string;
    kind: string;
    author_name: string;
    author_handle: string;
    subject: string;
    permalink: string;
    already_replied: boolean;
    source_post?: { caption?: string; status?: string } | null;
    transcript: Array<{ direction: string; author: string; text: string; at: string | null }>;
  };
  capabilities: Record<string, unknown>;
  can_reply: boolean;
  /** Set when the reply composer is blocked: dm_window_expired | dm_recipient_unknown. */
  reply_blocked_reason: string;
  reply_target_external_id: string;
};

export type SocialInboxFacets = {
  total: number;
  unread: number;
  by_platform: Record<string, number>;
  by_status: Record<string, number>;
  by_kind: Record<string, number>;
};

export type SocialInboxThreadsResponse = {
  ok: boolean;
  items: SocialInboxThread[];
  count: number;
  total: number;
  offset: number;
  limit: number;
  has_more: boolean;
  facets: SocialInboxFacets;
};

export type SocialInboxStatus = {
  ok: boolean;
  platforms: SocialInboxPlatformCapability[];
  facets: SocialInboxFacets;
  statuses: string[];
  draft_statuses: string[];
  priorities: string[];
  kinds: string[];
  draft_variants: Array<{ id: string; label: string; hint: string }>;
  approval_required: boolean;
};

export type SocialInboxFilters = {
  platform?: string;
  status?: string;
  kind?: string;
  priority?: string;
  assigned_to?: string;
  unread?: boolean;
  q?: string;
  sort?: "recent" | "oldest" | "unread";
  limit?: number;
  offset?: number;
};

export async function fetchSocialInboxStatus(): Promise<SocialInboxStatus> {
  const { data } = await api.get("/api/marketing/social/inbox/status");
  return data as SocialInboxStatus;
}

export async function syncSocialInbox(input?: {
  platforms?: string[];
  limit?: number;
}): Promise<Record<string, unknown>> {
  const { data } = await api.post("/api/marketing/social/inbox/sync", input || {});
  return data as Record<string, unknown>;
}

export async function fetchSocialInboxThreads(
  filters: SocialInboxFilters = {},
): Promise<SocialInboxThreadsResponse> {
  const params = new URLSearchParams();
  if (filters.platform) params.set("platform", filters.platform);
  if (filters.status) params.set("status", filters.status);
  if (filters.kind) params.set("kind", filters.kind);
  if (filters.priority) params.set("priority", filters.priority);
  if (filters.assigned_to) params.set("assigned_to", filters.assigned_to);
  if (typeof filters.unread === "boolean") params.set("unread", String(filters.unread));
  if (filters.q) params.set("q", filters.q);
  if (filters.sort) params.set("sort", filters.sort);
  if (filters.limit) params.set("limit", String(filters.limit));
  if (filters.offset) params.set("offset", String(filters.offset));
  const query = params.toString();
  const { data } = await api.get(
    `/api/marketing/social/inbox/threads${query ? `?${query}` : ""}`,
  );
  return data as SocialInboxThreadsResponse;
}

export async function fetchSocialInboxThread(
  threadId: string,
  markRead = false,
): Promise<SocialInboxThreadDetail> {
  const { data } = await api.get(
    `/api/marketing/social/inbox/threads/${threadId}?mark_read=${markRead ? "true" : "false"}`,
  );
  return data as SocialInboxThreadDetail;
}

export async function updateSocialInboxThread(
  threadId: string,
  patch: {
    status?: string;
    priority?: string;
    assigned_to?: string;
    unread?: boolean;
    tags?: string[];
  },
): Promise<{ ok: boolean; thread: SocialInboxThread }> {
  const { data } = await api.patch(`/api/marketing/social/inbox/threads/${threadId}`, patch);
  return data as { ok: boolean; thread: SocialInboxThread };
}

export async function generateSocialInboxDrafts(
  threadId: string,
  input?: { instruction?: string; tones?: string[] },
): Promise<{ ok: boolean; drafts: SocialInboxDraft[]; count: number; ai_used: boolean }> {
  const { data } = await api.post(
    `/api/marketing/social/inbox/threads/${threadId}/drafts`,
    input || {},
  );
  return data as { ok: boolean; drafts: SocialInboxDraft[]; count: number; ai_used: boolean };
}

export async function createSocialInboxManualDraft(
  threadId: string,
  input: { text: string; autoApprove?: boolean },
): Promise<{ ok: boolean; items: SocialInboxDraft[]; count: number }> {
  const { data } = await api.post(`/api/marketing/social/inbox/threads/${threadId}/drafts`, {
    text: input.text,
    auto_approve: Boolean(input.autoApprove),
  });
  return data as { ok: boolean; items: SocialInboxDraft[]; count: number };
}

export async function updateSocialInboxDraft(
  draftId: string,
  patch: { text?: string; status?: string; tone?: string },
): Promise<{ ok: boolean; draft: SocialInboxDraft }> {
  const { data } = await api.patch(`/api/marketing/social/inbox/drafts/${draftId}`, patch);
  return data as { ok: boolean; draft: SocialInboxDraft };
}

export async function approveSocialInboxDraft(
  draftId: string,
): Promise<{ ok: boolean; draft: SocialInboxDraft }> {
  const { data } = await api.post(`/api/marketing/social/inbox/drafts/${draftId}/approve`);
  return data as { ok: boolean; draft: SocialInboxDraft };
}

export async function rejectSocialInboxDraft(
  draftId: string,
): Promise<{ ok: boolean; draft: SocialInboxDraft }> {
  const { data } = await api.post(`/api/marketing/social/inbox/drafts/${draftId}/reject`);
  return data as { ok: boolean; draft: SocialInboxDraft };
}

export async function sendSocialInboxDraft(
  draftId: string,
  input?: { force?: boolean },
): Promise<{
  ok: boolean;
  reason: string;
  message?: string;
  vendor_reply_id?: string;
  draft: SocialInboxDraft | null;
  thread_status?: string;
}> {
  const { data } = await api.post(
    `/api/marketing/social/inbox/drafts/${draftId}/send`,
    input || {},
  );
  return data as {
    ok: boolean;
    reason: string;
    message?: string;
    vendor_reply_id?: string;
    draft: SocialInboxDraft | null;
    thread_status?: string;
  };
}

export type SocialCalendar = {
  ok: boolean;
  days: Record<string, SocialPost[]>;
  upcoming: SocialPost[];
  due: SocialPost[];
  counts: { posts: number; upcoming: number; due: number; days: number };
  week_start?: string;
  week_end?: string;
  ordered_days?: string[];
};

export async function fetchSocialCalendar(input?: {
  date_from?: string;
  date_to?: string;
  week_start?: string;
}): Promise<SocialCalendar> {
  const { data } = await api.get("/api/marketing/social/calendar", {
    params: {
      date_from: input?.date_from || undefined,
      date_to: input?.date_to || undefined,
      week_start: input?.week_start || undefined,
    },
  });
  return data as SocialCalendar;
}

export async function runSocialSchedulerTick(
  dryRun = false,
  mode: "local" | "vendor" = "local",
): Promise<Record<string, unknown>> {
  const { data } = await api.post("/api/marketing/social/scheduler/tick", null, {
    params: { dry_run: dryRun, mode },
  });
  return data as Record<string, unknown>;
}

export type SocialPublishStatus = {
  ok: boolean;
  live_enabled: boolean;
  default_mode: string;
  live_ready_count: number;
  platforms: Array<{
    platform: string;
    provider: string | null;
    connected: boolean;
    can_sandbox_publish: boolean;
    can_live_publish: boolean;
    preferred_mode: string;
    publish_api?: string;
    threads_user_id_set?: boolean;
    ig_user_id_set?: boolean;
    default_ig_image_set?: boolean;
    live_blocker?: string;
  }>;
  threads?: {
    api: string;
    create_path: string;
    publish_path: string;
    config_key: string;
    image_keys?: string[];
    video_keys?: string[];
    media_types?: string[];
    webhook_path?: string;
    webhooks_enabled?: boolean;
    webhook_verify_configured?: boolean;
    wait_mode?: string;
  };
  instagram?: {
    api: string;
    create_path: string;
    publish_path: string;
    config_key: string;
    image_keys?: string[];
    video_keys?: string[];
    media_types?: string[];
    reels_keys?: string[];
    share_to_feed_key?: string;
    cover_url_key?: string;
    carousel_min?: number;
    carousel_max?: number;
    status_path?: string;
    poll_until?: string;
    poll_max_attempts?: number;
    poll_interval_seconds?: number;
    wait_mode?: string;
    webhook_path?: string;
    webhooks_enabled?: boolean;
    webhook_verify_configured?: boolean;
  };
  ig_media_webhooks?: {
    enabled: boolean;
    verify_token_configured: boolean;
    app_secret_configured: boolean;
    wait_mode: string;
    path: string;
    pending_statuses: number;
    received_total: number;
  };
  threads_webhooks?: {
    enabled: boolean;
    verify_token_configured: boolean;
    app_secret_configured: boolean;
    wait_mode: string;
    path: string;
    pending_statuses: number;
    received_total: number;
    recent?: Array<{ entity_id?: string; status_code?: string; field?: string }>;
  };
  linkedin?: {
    api: string;
    text_path: string;
    image_register: string;
    video_register?: string;
    config_key: string;
    image_keys?: string[];
    video_keys?: string[];
    media_types?: string[];
    recipes?: { image?: string; video?: string };
  };
  twitter?: {
    api: string;
    text_path: string;
    media_upload: string;
    image_keys?: string[];
    video_keys?: string[];
    media_types?: string[];
    video_upload?: string;
    media_category?: string;
  };
  facebook?: {
    api: string;
    text_path: string;
    photo_path: string;
    video_path?: string;
    config_key: string;
    image_keys?: string[];
    video_keys?: string[];
    media_types?: string[];
    video_file_url_key?: string;
  };
  scheduler?: Record<string, unknown>;
  notes?: string[];
};

export async function fetchSocialPublishStatus(): Promise<SocialPublishStatus> {
  const { data } = await api.get("/api/marketing/social/publish/status");
  return data as SocialPublishStatus;
}

export async function publishSocialPostNow(
  postId: string,
  opts?: { mode?: "local" | "vendor"; dry_run?: boolean; retry?: boolean; force?: boolean },
): Promise<{
  ok: boolean;
  mode: string;
  post: SocialPost;
  vendor?: Record<string, unknown>;
  retry?: boolean;
  force?: boolean;
  history_count?: number;
  message?: string;
  reason?: string;
}> {
  const { data } = await api.post(`/api/marketing/social/posts/${postId}/publish`, null, {
    params: {
      mode: opts?.mode || "vendor",
      dry_run: opts?.dry_run || false,
      retry: opts?.retry || false,
      force: opts?.force || false,
    },
  });
  return data as {
    ok: boolean;
    mode: string;
    post: SocialPost;
    vendor?: Record<string, unknown>;
    retry?: boolean;
    force?: boolean;
    history_count?: number;
    message?: string;
    reason?: string;
  };
}

export type SocialPublishHistoryItem = {
  post_id: string;
  platform?: string;
  status?: string;
  caption_preview?: string;
  at?: string;
  ok?: boolean;
  mode?: string;
  reason?: string;
  api?: string;
  vendor_post_id?: string;
  retry?: boolean;
  can_retry?: boolean;
  can_force?: boolean;
};

export type SocialPublishHistory = {
  ok: boolean;
  items: SocialPublishHistoryItem[];
  count: number;
  total_unfiltered?: number;
  failed_count: number;
  retryable_count: number;
  forceable_count?: number;
  platform_counts?: Record<string, number>;
  filters?: {
    platform?: string | null;
    outcome?: string;
    limit?: number;
  };
};

export async function fetchSocialPublishHistory(
  limit = 40,
  opts?: { platform?: string; outcome?: "all" | "failed" | "ok" | "retryable" },
): Promise<SocialPublishHistory> {
  const { data } = await api.get("/api/marketing/social/publish/history", {
    params: {
      limit,
      ...(opts?.platform ? { platform: opts.platform } : {}),
      ...(opts?.outcome && opts.outcome !== "all" ? { outcome: opts.outcome } : {}),
    },
  });
  return data as SocialPublishHistory;
}

export async function fetchAds(): Promise<AdCampaign[]> {
  const { data } = await api.get("/api/marketing/ads");
  return (data?.items || []) as AdCampaign[];
}

export async function fetchAd(adId: string): Promise<AdCampaign> {
  const { data } = await api.get(`/api/marketing/ads/${adId}`);
  return data as AdCampaign;
}

export async function fetchAdHub(adId: string): Promise<AdHubPayload> {
  const { data } = await api.get(`/api/marketing/ads/${adId}/hub`);
  return data as AdHubPayload;
}

export async function createAd(input: {
  name: string;
  network: string;
  objective: string;
  daily_budget: number;
  status: string;
  geo?: string[];
  keywords?: string[];
  schedule?: { starts_at?: string | null; ends_at?: string | null };
}): Promise<AdCampaign> {
  const { data } = await api.post("/api/marketing/ads", input);
  return data as AdCampaign;
}

export async function updateAd(
  adId: string,
  input: {
    name?: string;
    network?: string;
    objective?: string;
    daily_budget?: number;
    status?: string;
    creative?: string;
    targeting?: Record<string, unknown>;
    schedule?: Record<string, unknown>;
    optimization_event?: string;
  },
): Promise<AdCampaign> {
  const { data } = await api.patch(`/api/marketing/ads/${adId}`, input);
  return data as AdCampaign;
}

export async function cloneAd(adId: string, name?: string): Promise<AdCampaign> {
  const { data } = await api.post(`/api/marketing/ads/${adId}/clone`, { name: name || undefined });
  return data as AdCampaign;
}

export async function deleteAd(adId: string): Promise<{ ok: boolean }> {
  const { data } = await api.delete(`/api/marketing/ads/${adId}`);
  return data as { ok: boolean };
}

export async function linkAdCreative(
  adId: string,
  input: { creative_id: string; ad_group_id?: string; ad_unit_id?: string },
): Promise<{ ok: boolean; ad: AdCampaign }> {
  const { data } = await api.post(`/api/marketing/ads/${adId}/creatives/link`, input);
  return data as { ok: boolean; ad: AdCampaign };
}

export async function unlinkAdCreative(
  adId: string,
  input: { creative_id: string; ad_group_id?: string; ad_unit_id?: string },
): Promise<{ ok: boolean; ad: AdCampaign }> {
  const { data } = await api.post(`/api/marketing/ads/${adId}/creatives/unlink`, input);
  return data as { ok: boolean; ad: AdCampaign };
}

export async function upsertAdUnit(
  adId: string,
  input: {
    ad_group_id: string;
    ad_unit_id?: string;
    name?: string;
    headline?: string;
    description?: string;
    final_url?: string;
    cta?: string;
    status?: string;
  },
): Promise<AdCampaign> {
  const { data } = await api.post(`/api/marketing/ads/${adId}/ad-units`, input);
  return data as AdCampaign;
}

export async function upsertAdGroup(
  adId: string,
  input: { ad_group_id?: string; name?: string; status?: string; bid_strategy?: string },
): Promise<AdCampaign> {
  const { data } = await api.post(`/api/marketing/ads/${adId}/ad-groups`, input);
  return data as AdCampaign;
}

export async function generateAdBrief(adId: string): Promise<{
  ok: boolean;
  brief: Record<string, unknown>;
  ad: AdCampaign;
}> {
  const { data } = await api.post(`/api/marketing/ads/${adId}/brief`);
  return data as { ok: boolean; brief: Record<string, unknown>; ad: AdCampaign };
}

export async function buildAdUtm(
  adId: string,
  input: { base_url?: string; source?: string; medium?: string; content?: string; persist?: boolean },
): Promise<{ ok: boolean; params: Record<string, string>; url: string }> {
  const { data } = await api.post(`/api/marketing/ads/${adId}/utm`, input);
  return data as { ok: boolean; params: Record<string, string>; url: string };
}

export async function syncAdToGoogle(
  adId: string,
  force = false,
): Promise<{ ok: boolean; mode?: string; message?: string; google_sync?: Record<string, unknown>; ad?: AdCampaign }> {
  const { data } = await api.post(`/api/marketing/ads/${adId}/sync/google`, { force });
  return data as {
    ok: boolean;
    mode?: string;
    message?: string;
    google_sync?: Record<string, unknown>;
    ad?: AdCampaign;
  };
}

export async function syncAdToMeta(
  adId: string,
  force = false,
): Promise<{ ok: boolean; mode?: string; message?: string; vendor_sync?: Record<string, unknown>; ad?: AdCampaign }> {
  const { data } = await api.post(`/api/marketing/ads/${adId}/sync/meta`, { force });
  return data as {
    ok: boolean;
    mode?: string;
    message?: string;
    vendor_sync?: Record<string, unknown>;
    ad?: AdCampaign;
  };
}

export async function syncAdToLinkedin(
  adId: string,
  force = false,
): Promise<{ ok: boolean; mode?: string; message?: string; vendor_sync?: Record<string, unknown>; ad?: AdCampaign }> {
  const { data } = await api.post(`/api/marketing/ads/${adId}/sync/linkedin`, { force });
  return data as {
    ok: boolean;
    mode?: string;
    message?: string;
    vendor_sync?: Record<string, unknown>;
    ad?: AdCampaign;
  };
}

export async function syncAdToNetwork(
  adId: string,
  force = false,
): Promise<{
  ok: boolean;
  mode?: string;
  network?: string;
  message?: string;
  vendor_sync?: Record<string, unknown>;
  ad?: AdCampaign;
}> {
  const { data } = await api.post(`/api/marketing/ads/${adId}/sync`, { force });
  return data as {
    ok: boolean;
    mode?: string;
    network?: string;
    message?: string;
    vendor_sync?: Record<string, unknown>;
    ad?: AdCampaign;
  };
}

export async function approveAd(
  adId: string,
  input?: { approved?: boolean; note?: string; approved_by?: string },
): Promise<{ ok: boolean; ad: AdCampaign }> {
  const { data } = await api.post(`/api/marketing/ads/${adId}/approve`, input || { approved: true });
  return data as { ok: boolean; ad: AdCampaign };
}

export async function setAdStatus(
  adId: string,
  status: string,
  syncGoogle = true,
): Promise<{ ok: boolean; ad: AdCampaign; google?: Record<string, unknown> }> {
  const { data } = await api.post(`/api/marketing/ads/${adId}/status`, {
    status,
    sync_google: syncGoogle,
  });
  return data as { ok: boolean; ad: AdCampaign; google?: Record<string, unknown> };
}

export async function updateAdBudgetRules(
  adId: string,
  input: { max_daily_spend?: number | null; pause_on_overspend?: boolean; alert_at_pct?: number },
): Promise<{ ok: boolean; ad: AdCampaign }> {
  const { data } = await api.post(`/api/marketing/ads/${adId}/budget-rules`, input);
  return data as { ok: boolean; ad: AdCampaign };
}

export async function refreshAdMetrics(
  adId: string,
  days = 7,
): Promise<{
  ok: boolean;
  metrics: Record<string, unknown>;
  auto_paused?: boolean;
  performance?: Record<string, unknown>;
  ad: AdCampaign;
}> {
  const { data } = await api.post(`/api/marketing/ads/${adId}/refresh-metrics`, { days });
  return data as {
    ok: boolean;
    metrics: Record<string, unknown>;
    auto_paused?: boolean;
    performance?: Record<string, unknown>;
    ad: AdCampaign;
  };
}

export async function fetchGoogleAdsReadiness(): Promise<{
  ok: boolean;
  oauth_connected?: boolean;
  customer_id?: string | null;
  mode?: string;
  detail?: string;
  provider?: string;
  network?: string;
}> {
  const { data } = await api.get("/api/marketing/ads/google/readiness");
  return data as {
    ok: boolean;
    oauth_connected?: boolean;
    customer_id?: string | null;
    mode?: string;
    detail?: string;
    provider?: string;
    network?: string;
  };
}

export async function fetchMetaAdsReadiness(): Promise<{
  ok: boolean;
  oauth_connected?: boolean;
  ad_account_id?: string | null;
  mode?: string;
  detail?: string;
}> {
  const { data } = await api.get("/api/marketing/ads/meta/readiness");
  return data as {
    ok: boolean;
    oauth_connected?: boolean;
    ad_account_id?: string | null;
    mode?: string;
    detail?: string;
  };
}

export async function fetchLinkedinAdsReadiness(): Promise<{
  ok: boolean;
  oauth_connected?: boolean;
  ad_account_id?: string | null;
  mode?: string;
  detail?: string;
}> {
  const { data } = await api.get("/api/marketing/ads/linkedin/readiness");
  return data as {
    ok: boolean;
    oauth_connected?: boolean;
    ad_account_id?: string | null;
    mode?: string;
    detail?: string;
  };
}

export async function fetchCreatives(kind?: string): Promise<Creative[]> {
  const { data } = await api.get("/api/marketing/creatives", { params: kind ? { kind } : {} });
  return (data?.items || []) as Creative[];
}

export type MediaGeneration = {
  id: string;
  tenant_id?: string;
  kind: "image" | "video" | string;
  mode?: string;
  status: "queued" | "processing" | "completed" | "failed" | string;
  provider?: string;
  model?: string;
  prompt: string;
  aspect_ratio?: string;
  resolution?: string;
  duration_seconds?: number | null;
  parent_id?: string | null;
  job_id?: string | null;
  mime_type?: string;
  file_ext?: string;
  file_size?: number;
  error?: string | null;
  download_url?: string | null;
  preview_url?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  meta?: Record<string, unknown>;
};

export type MediaCapabilities = {
  ok: boolean;
  kind: string;
  provider: {
    id: string;
    label: string;
    live?: boolean;
    configured?: boolean;
    modes?: string[];
    aspect_ratios?: string[];
    resolutions?: string[];
    durations?: number[];
    max_images?: number;
    supports_reference?: boolean;
    supports_edit?: boolean;
    models?: string[];
    billing?: string;
    note?: string;
  };
};

export async function fetchImageMediaCapabilities(): Promise<MediaCapabilities> {
  const { data } = await api.get("/api/marketing/media/image/capabilities");
  return data as MediaCapabilities;
}

export async function fetchVideoMediaCapabilities(): Promise<MediaCapabilities> {
  const { data } = await api.get("/api/marketing/media/video/capabilities");
  return data as MediaCapabilities;
}

export async function fetchImageMediaHistory(limit = 40): Promise<MediaGeneration[]> {
  const { data } = await api.get("/api/marketing/media/image/history", { params: { limit } });
  return (data?.items || []) as MediaGeneration[];
}

export async function fetchVideoMediaHistory(limit = 40): Promise<MediaGeneration[]> {
  const { data } = await api.get("/api/marketing/media/video/history", { params: { limit } });
  return (data?.items || []) as MediaGeneration[];
}

export async function fetchMediaGeneration(id: string): Promise<MediaGeneration> {
  const { data } = await api.get(`/api/marketing/media/${id}`);
  return data.item as MediaGeneration;
}

export async function generateMediaImage(input: {
  prompt: string;
  aspect_ratio?: string;
  resolution?: string;
  parent_id?: string;
  mode?: string;
  reference?: File | null;
}): Promise<MediaGeneration> {
  const form = new FormData();
  form.append("prompt", input.prompt);
  if (input.aspect_ratio) form.append("aspect_ratio", input.aspect_ratio);
  if (input.resolution) form.append("resolution", input.resolution);
  if (input.parent_id) form.append("parent_id", input.parent_id);
  if (input.mode) form.append("mode", input.mode);
  if (input.reference) form.append("reference", input.reference);
  const { data } = await api.post("/api/marketing/media/image/generate", form, {
    timeout: 180000,
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.item as MediaGeneration;
}

export async function editMediaImage(id: string, prompt: string): Promise<MediaGeneration> {
  const form = new FormData();
  form.append("prompt", prompt);
  const { data } = await api.post(`/api/marketing/media/image/${id}/edit`, form, {
    timeout: 180000,
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.item as MediaGeneration;
}

export async function regenerateMediaImage(
  id: string,
  input?: { prompt?: string; aspect_ratio?: string; resolution?: string },
): Promise<MediaGeneration> {
  const form = new FormData();
  if (input?.prompt) form.append("prompt", input.prompt);
  if (input?.aspect_ratio) form.append("aspect_ratio", input.aspect_ratio);
  if (input?.resolution) form.append("resolution", input.resolution);
  const { data } = await api.post(`/api/marketing/media/image/${id}/regenerate`, form, {
    timeout: 180000,
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.item as MediaGeneration;
}

export async function generateMediaVideo(input: {
  prompt: string;
  mode?: "text" | "image_to_video" | string;
  aspect_ratio?: string;
  resolution?: string;
  duration_seconds?: number;
  parent_id?: string;
  reference?: File | null;
  run_inline?: boolean;
}): Promise<MediaGeneration> {
  const form = new FormData();
  form.append("prompt", input.prompt);
  if (input.mode) form.append("mode", input.mode);
  if (input.aspect_ratio) form.append("aspect_ratio", input.aspect_ratio);
  if (input.resolution) form.append("resolution", input.resolution);
  if (input.duration_seconds != null) form.append("duration_seconds", String(input.duration_seconds));
  if (input.parent_id) form.append("parent_id", input.parent_id);
  if (input.run_inline) form.append("run_inline", "true");
  if (input.reference) form.append("reference", input.reference);
  const { data } = await api.post("/api/marketing/media/video/generate", form, {
    timeout: 180000,
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.item as MediaGeneration;
}

export async function regenerateMediaVideo(
  id: string,
  input?: { prompt?: string; run_inline?: boolean },
): Promise<MediaGeneration> {
  const form = new FormData();
  if (input?.prompt) form.append("prompt", input.prompt);
  if (input?.run_inline) form.append("run_inline", "true");
  const { data } = await api.post(`/api/marketing/media/video/${id}/regenerate`, form, {
    timeout: 180000,
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.item as MediaGeneration;
}

/** Authenticated blob URL for media preview/download (revoker should call URL.revokeObjectURL). */
export async function fetchMediaBlobUrl(generationId: string): Promise<string> {
  const { data } = await api.get(`/api/marketing/media/${generationId}/download`, {
    responseType: "blob",
    timeout: 120000,
  });
  return URL.createObjectURL(data as Blob);
}

export async function downloadMediaFile(generationId: string, filename?: string): Promise<void> {
  const { data, headers } = await api.get(`/api/marketing/media/${generationId}/download`, {
    responseType: "blob",
    timeout: 120000,
  });
  const blob = data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const disposition = String(headers?.["content-disposition"] || "");
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  a.download = filename || match?.[1] || `${generationId}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function deleteMediaGeneration(generationId: string): Promise<{ ok: boolean; id: string }> {
  const { data } = await api.delete(`/api/marketing/media/${generationId}`);
  return data as { ok: boolean; id: string };
}

export async function fetchImageGenProviders(): Promise<{
  items: ImageGenProvider[];
  formats?: { id: string; hint: string }[];
  mode?: string;
  disclaimer?: string;
}> {
  const { data } = await api.get("/api/marketing/creatives/image-gen/providers");
  return data as {
    items: ImageGenProvider[];
    formats?: { id: string; hint: string }[];
    mode?: string;
    disclaimer?: string;
  };
}

export async function generateImagePrompt(input: {
  title: string;
  brief?: string;
  format?: string;
  provider?: string;
  use_brand_voice?: boolean;
  brand_voice?: string;
  creative_id?: string;
  apply_to_creative?: boolean;
}): Promise<ImageGenResult> {
  const { data } = await api.post("/api/marketing/creatives/image-gen/generate", input);
  return data as ImageGenResult;
}

export async function fetchVideoScriptTemplates(): Promise<{
  items: VideoScriptTemplate[];
  mode?: string;
  disclaimer?: string;
}> {
  const { data } = await api.get("/api/marketing/creatives/video-script/templates");
  return data as { items: VideoScriptTemplate[]; mode?: string; disclaimer?: string };
}

export async function generateVideoScript(input: {
  title: string;
  brief?: string;
  format?: string;
  use_brand_voice?: boolean;
  brand_voice?: string;
  creative_id?: string;
  apply_to_creative?: boolean;
}): Promise<VideoScriptResult> {
  const { data } = await api.post("/api/marketing/creatives/video-script/generate", input);
  return data as VideoScriptResult;
}

export async function exportVideoStoryboard(creativeId: string): Promise<{
  ok: boolean;
  creative_id: string;
  filename: string;
  content: string;
  beat_count?: number;
  mode?: string;
  disclaimer?: string;
}> {
  const { data } = await api.get(`/api/marketing/creatives/${creativeId}/storyboard-export`);
  return data as {
    ok: boolean;
    creative_id: string;
    filename: string;
    content: string;
    beat_count?: number;
    mode?: string;
    disclaimer?: string;
  };
}

export async function createCreative(input: {
  kind: string;
  title: string;
  format: string;
  brief: string;
}): Promise<Creative> {
  const { data } = await api.post("/api/marketing/creatives", input);
  return data as Creative;
}

export async function updateCreative(
  creativeId: string,
  input: {
    title?: string;
    format?: string;
    brief?: string;
    script?: string;
    status?: string;
  },
): Promise<Creative> {
  const { data } = await api.patch(`/api/marketing/creatives/${creativeId}`, input);
  return data as Creative;
}

export async function exportCreative(creativeId: string): Promise<CreativeExport> {
  const { data } = await api.get(`/api/marketing/creatives/${creativeId}/export`);
  return data as CreativeExport;
}

export async function fetchLandingPages(): Promise<LandingPage[]> {
  const { data } = await api.get("/api/marketing/landing-pages");
  return (data?.items || []) as LandingPage[];
}

export async function fetchLandingTemplates(): Promise<LandingTemplate[]> {
  const { data } = await api.get("/api/marketing/landing-pages/templates");
  return (data?.items || []) as LandingTemplate[];
}

export async function fetchLandingBlockTypes(): Promise<LandingBlockType[]> {
  const { data } = await api.get("/api/marketing/landing-pages/block-types");
  return (data?.items || []) as LandingBlockType[];
}

export async function fetchLandingPageHub(pageId: string): Promise<LandingHub> {
  const { data } = await api.get(`/api/marketing/landing-pages/${pageId}/hub`);
  return data as LandingHub;
}

export async function createLandingPage(input: {
  name: string;
  slug: string;
  headline: string;
  template: string;
  blocks?: LandingBlock[];
  campaign_id?: string;
}): Promise<LandingPage> {
  const { data } = await api.post("/api/marketing/landing-pages", input);
  return data as LandingPage;
}

export async function updateLandingPage(
  pageId: string,
  input: {
    name?: string;
    slug?: string;
    headline?: string;
    template?: string;
    status?: string;
    html_outline?: string;
    blocks?: LandingBlock[];
    utm?: Record<string, string>;
    seo?: Record<string, unknown>;
    campaign_id?: string;
    meta?: Record<string, unknown>;
  },
): Promise<LandingPage> {
  const { data } = await api.patch(`/api/marketing/landing-pages/${pageId}`, input);
  return data as LandingPage;
}

export async function deleteLandingPage(pageId: string): Promise<{ ok: boolean; id: string }> {
  const { data } = await api.delete(`/api/marketing/landing-pages/${pageId}`);
  return data as { ok: boolean; id: string };
}

export async function publishLandingPage(
  pageId: string,
  input?: { force?: boolean },
): Promise<LandingPublishResult> {
  const { data } = await api.post(`/api/marketing/landing-pages/${pageId}/publish`, input || {});
  return data as LandingPublishResult;
}

export async function submitLandingLead(
  pageId: string,
  input: { fields: Record<string, string>; dry_run?: boolean },
): Promise<LandingLeadResult> {
  const { data } = await api.post(`/api/marketing/landing-pages/${pageId}/leads`, input);
  return data as LandingLeadResult;
}

export async function trackLandingEvent(
  pageId: string,
  input: { event: "view" | "submit" | "conversion"; utm?: Record<string, string> },
): Promise<{ ok: boolean; analytics?: LandingAnalytics }> {
  const { data } = await api.post(`/api/marketing/landing-pages/${pageId}/events`, input);
  return data as { ok: boolean; analytics?: LandingAnalytics };
}

export async function exportLandingPage(pageId: string): Promise<LandingPageExport> {
  const { data } = await api.get(`/api/marketing/landing-pages/${pageId}/export`);
  return data as LandingPageExport;
}

export async function previewLandingPage(pageId: string): Promise<LandingPreview> {
  const { data } = await api.get(`/api/marketing/landing-pages/${pageId}/preview`);
  return data as LandingPreview;
}

export async function fetchLandingReadinessChecks(): Promise<{
  items: Array<{ id: string; label: string; severity: string }>;
  mode?: string;
  disclaimer?: string;
}> {
  const { data } = await api.get("/api/marketing/landing-pages/readiness-checks");
  return data as {
    items: Array<{ id: string; label: string; severity: string }>;
    mode?: string;
    disclaimer?: string;
  };
}

export async function fetchLandingReadiness(pageId: string): Promise<LandingReadiness> {
  const { data } = await api.get(`/api/marketing/landing-pages/${pageId}/readiness`);
  return data as LandingReadiness;
}

export async function listWebsiteScanners(): Promise<WebsiteScannerInfo[]> {
  const { data } = await api.get("/api/marketing/website/scanners");
  return (data?.items || []) as WebsiteScannerInfo[];
}

export async function scanWebsite(
  url: string,
  opts?: { html?: string; fetch?: boolean; scanner?: string },
): Promise<WebsiteScan> {
  const { data } = await api.post("/api/marketing/website/scan", {
    url,
    html: opts?.html || undefined,
    fetch: Boolean(opts?.fetch),
    scanner: opts?.scanner || undefined,
  });
  return data as WebsiteScan;
}

export async function fetchAnalyticsSummary(): Promise<AnalyticsSummary> {
  const { data } = await api.get("/api/marketing/analytics/summary");
  return data as AnalyticsSummary;
}

export async function fetchRecommendations(status?: string): Promise<RecommendationsResponse> {
  const { data } = await api.get("/api/marketing/recommendations", {
    params: status ? { status } : undefined,
  });
  return {
    ok: data?.ok,
    items: (data?.items || []) as Recommendation[],
    counts: (data?.counts || {}) as Record<string, number>,
    snooze_options: (data?.snooze_options || []) as number[],
  };
}

export async function fetchRecommendationHistory(
  recommendationId: string,
): Promise<{
  ok: boolean;
  recommendation_id: string;
  status?: string;
  items: RecommendationHistoryEntry[];
  count: number;
  snooze_options?: number[];
}> {
  const { data } = await api.get(`/api/marketing/recommendations/${recommendationId}/history`);
  return data as {
    ok: boolean;
    recommendation_id: string;
    status?: string;
    items: RecommendationHistoryEntry[];
    count: number;
    snooze_options?: number[];
  };
}

export async function applyRecommendationAction(
  recommendationId: string,
  input: { action: RecommendationAction; note?: string; snooze_days?: number },
): Promise<{ ok: boolean; action: string; recommendation: Recommendation }> {
  const { data } = await api.post(`/api/marketing/recommendations/${recommendationId}/action`, input);
  return data as { ok: boolean; action: string; recommendation: Recommendation };
}

export async function fetchWorkflows(): Promise<Workflow[]> {
  const { data } = await api.get("/api/marketing/workflows");
  return (data?.items || []) as Workflow[];
}

export type ApprovalItem = {
  id: string;
  kind: "social" | "draft" | string;
  title: string;
  snippet: string;
  status: string;
  path: string;
  platform?: string;
  channel?: string;
  created_at?: string;
};

export type ApprovalsQueue = {
  ok: boolean;
  items: ApprovalItem[];
  count: number;
  counts: { social: number; draft: number };
  filters?: { kind?: string };
};

export async function fetchApprovals(opts?: {
  kind?: "all" | "social" | "draft";
}): Promise<ApprovalsQueue> {
  const { data } = await api.get("/api/marketing/approvals", {
    params: opts?.kind && opts.kind !== "all" ? { kind: opts.kind } : {},
  });
  return data as ApprovalsQueue;
}

export type ApprovalHistoryItem = {
  id?: string;
  item_id?: string;
  kind: string;
  decision: string;
  note?: string;
  at?: string;
  path?: string;
};

export type ApprovalHistory = {
  ok: boolean;
  items: ApprovalHistoryItem[];
  count: number;
  counts: { approve: number; reject: number; social: number; draft: number };
  filters?: { kind?: string; decision?: string; limit?: number };
  path?: string;
};

export async function fetchApprovalsHistory(opts?: {
  limit?: number;
  kind?: "all" | "social" | "draft";
  decision?: "all" | "approve" | "reject";
}): Promise<ApprovalHistory> {
  const { data } = await api.get("/api/marketing/approvals/history", {
    params: {
      limit: opts?.limit ?? 20,
      ...(opts?.kind && opts.kind !== "all" ? { kind: opts.kind } : {}),
      ...(opts?.decision && opts.decision !== "all" ? { decision: opts.decision } : {}),
    },
  });
  return data as ApprovalHistory;
}

export async function decideApproval(input: {
  id: string;
  kind: "social" | "draft";
  decision: "approve" | "reject";
  note?: string;
}): Promise<Record<string, unknown>> {
  const { data } = await api.post(`/api/marketing/approvals/${input.id}/decide`, {
    kind: input.kind,
    decision: input.decision,
    note: input.note || "",
  });
  return data as Record<string, unknown>;
}

export async function createWorkflow(input: { name: string; trigger: string }): Promise<Workflow> {
  const { data } = await api.post("/api/marketing/workflows", input);
  return data as Workflow;
}

export async function fetchIntegrations(): Promise<Integration[]> {
  const { data } = await api.get("/api/marketing/integrations");
  return (data?.items || []) as Integration[];
}

export async function upsertIntegration(input: {
  provider: string;
  status: string;
}): Promise<Integration> {
  const { data } = await api.post("/api/marketing/integrations", input);
  return data as Integration;
}

export async function fetchSettings(): Promise<MarketingSettings> {
  const { data } = await api.get("/api/marketing/settings");
  return data as MarketingSettings;
}

export async function saveSettings(input: Partial<MarketingSettings>): Promise<MarketingSettings> {
  const { data } = await api.put("/api/marketing/settings", input);
  return data as MarketingSettings;
}

export type QuotaUsage = {
  tenant_id: string;
  day: string;
  backend: string;
  rate_limit: { per_minute: number; enabled: boolean };
  quotas: Array<{ kind: string; limit: number; used: number; remaining: number }>;
};

export async function fetchQuotaUsage(): Promise<QuotaUsage> {
  const { data } = await api.get("/api/marketing/quotas/usage");
  return data as QuotaUsage;
}

export type ObservabilityAlert = {
  id: string;
  severity: string;
  category: string;
  provider?: string;
  title: string;
  detail?: string;
  created_at?: string;
};

export type ObservabilitySummary = {
  tenant_id: string;
  generated_at: string;
  integrations: {
    total: number;
    connected: number;
    oauth_connected: number;
    live_sync: number;
    fallback_sync: number;
    sandbox_sync: number;
  };
  events: { sync_count: number; validate_count: number; audit_count: number };
  alerts: { count: number; warnings: number; info: number };
  recent_alerts: ObservabilityAlert[];
};

export async function fetchObservabilitySummary(): Promise<ObservabilitySummary> {
  const { data } = await api.get("/api/marketing/observability/summary");
  return data as ObservabilitySummary;
}

export async function fetchObservabilityAlerts(): Promise<ObservabilityAlert[]> {
  const { data } = await api.get("/api/marketing/observability/alerts");
  return (data?.items || []) as ObservabilityAlert[];
}

export type AuditEvent = {
  id: string;
  tenant_id?: string;
  action: string;
  entity_type?: string;
  entity_id?: string;
  actor?: string | null;
  detail?: Record<string, unknown>;
  created_at: string;
};

export async function fetchObservabilityEvents(input?: {
  action?: string;
  limit?: number;
}): Promise<AuditEvent[]> {
  const { data } = await api.get("/api/marketing/observability/events", {
    params: {
      action: input?.action || undefined,
      limit: input?.limit ?? 50,
    },
  });
  return (data?.items || []) as AuditEvent[];
}

export type OperatorStatus = {
  ok: boolean;
  version: string;
  release: string;
  capabilities_count: number;
  constraints?: Record<string, boolean | string>;
  ai: { ok?: boolean; enabled?: boolean; mode?: string };
  crm: { ok?: boolean; read_enabled?: boolean; write_enabled?: boolean };
  jobs: {
    enabled?: boolean;
    backend?: string;
    worker?: { enabled?: boolean; heartbeat?: { ok?: boolean; processed?: number } };
  };
  oauth: {
    live_ready_count?: number;
    total_providers?: number;
    sandbox_allowed?: boolean;
    require_live?: boolean;
    capability?: string;
  };
  social_publish?: {
    live_enabled?: boolean;
    default_mode?: string;
    live_ready_count?: number;
    platform_count?: number;
    live_ready_platforms?: string[];
    sandbox_platforms?: string[];
    live_blockers?: Array<{ platform?: string; blocker?: string }>;
    instagram_wait_mode?: string;
    threads_wait_mode?: string;
    scheduler?: {
      live_enabled?: boolean;
      default_tick_mode?: string;
      modes?: string[];
      last_tick?: unknown;
    };
    publish_history?: {
      items?: Array<{
        post_id?: string;
        platform?: string;
        ok?: boolean;
        mode?: string;
        at?: string;
      }>;
      count?: number;
      failed_count?: number;
      retryable_count?: number;
    };
  };
  ig_media_webhooks?: {
    enabled?: boolean;
    verify_token_configured?: boolean;
    app_secret_configured?: boolean;
    wait_mode?: string;
    path?: string;
    pending_statuses?: number;
    received_total?: number;
  };
  threads_webhooks?: {
    enabled?: boolean;
    verify_token_configured?: boolean;
    app_secret_configured?: boolean;
    wait_mode?: string;
    path?: string;
    pending_statuses?: number;
    received_total?: number;
    recent?: Array<{ entity_id?: string; status_code?: string; field?: string }>;
  };
  release_readiness?: {
    ready?: boolean;
    summary?: { pass?: number; warn?: number; fail?: number; total?: number };
    paths?: Record<string, string>;
  };
  attention?: {
    count?: number;
    summary?: { high?: number; medium?: number; low?: number };
    items?: Array<{
      id: string;
      severity: string;
      title: string;
      detail?: string | null;
      path?: string | null;
    }>;
  };
  approvals?: {
    count?: number;
    counts?: { social?: number; draft?: number };
  };
  observability?: {
    integrations?: Record<string, number>;
    events?: Record<string, number>;
    alerts?: Record<string, number>;
  };
  modules?: string[];
};

export async function fetchOperatorStatus(): Promise<OperatorStatus> {
  const { data } = await api.get("/api/marketing/operator/status");
  return data as OperatorStatus;
}

export type OperatorAttentionItem = {
  id: string;
  severity: string;
  title: string;
  detail?: string | null;
  path?: string | null;
};

export type OperatorAttention = {
  ok: boolean;
  count: number;
  summary: { high: number; medium: number; low: number };
  items: OperatorAttentionItem[];
  path?: string;
};

export async function fetchOperatorAttention(): Promise<OperatorAttention> {
  const { data } = await api.get("/api/marketing/operator/attention");
  return data as OperatorAttention;
}

export type PublishDayDigest = {
  ok: boolean;
  date: string;
  days?: string[];
  count: number;
  summary: { high: number; medium: number; low: number };
  items: OperatorAttentionItem[];
  mode?: string;
  publish_ready?: boolean;
  quiet_skip?: {
    skip: boolean;
    quiet_skip_opt_in?: boolean;
    quiet_hours_active?: boolean;
    reason?: string;
  };
  path?: string;
  disclaimer?: string;
};

export async function fetchPublishDayDigest(opts?: {
  date?: string;
  include_tomorrow?: boolean;
}): Promise<PublishDayDigest> {
  const { data } = await api.get("/api/marketing/operator/publish-day-digest", {
    params: {
      ...(opts?.date ? { date: opts.date } : {}),
      ...(opts?.include_tomorrow === false ? { include_tomorrow: false } : {}),
    },
  });
  return data as PublishDayDigest;
}

export type PublishDayDigestNotify = {
  ok: boolean;
  notified: boolean;
  skipped: boolean;
  dry_run?: boolean;
  reason?: string;
  publish_ready?: boolean;
  quiet_skip?: PublishDayDigest["quiet_skip"];
  preview?: { title?: string; description?: string; priority?: string };
  disclaimer?: string;
};

export async function notifyPublishDayDigest(opts?: {
  date?: string;
  include_tomorrow?: boolean;
  force?: boolean;
  dry_run?: boolean;
}): Promise<PublishDayDigestNotify> {
  const { data } = await api.post("/api/marketing/operator/publish-day-digest/notify", null, {
    params: {
      ...(opts?.date ? { date: opts.date } : {}),
      ...(opts?.include_tomorrow === false ? { include_tomorrow: false } : {}),
      ...(opts?.force ? { force: true } : {}),
      ...(opts?.dry_run ? { dry_run: true } : {}),
    },
  });
  return data as PublishDayDigestNotify;
}

export type ReadinessExportPack = {
  ok: boolean;
  product?: string;
  version?: string;
  release?: string;
  generated_at?: string;
  date?: string;
  host_ready: boolean;
  publish_ready: boolean;
  mode?: string;
  disclaimer?: string;
  sections: {
    landing: {
      page_count: number;
      ready_count: number;
      not_ready_count: number;
      host_ready: boolean;
      pages: Array<{
        id?: string;
        name?: string;
        slug?: string;
        status?: string;
        ready?: boolean;
        host_ready?: boolean;
        summary?: { pass?: number; fail?: number; warn?: number; total?: number };
      }>;
      disclaimer?: string;
    };
    calendar: {
      date?: string;
      item_count?: number;
      ready?: boolean;
      publish_ready?: boolean;
      checks?: Array<{ id?: string; label?: string; ok?: boolean; message?: string }>;
      disclaimer?: string;
    };
    attention: {
      count?: number;
      summary?: { high?: number; medium?: number; low?: number };
      items?: OperatorAttentionItem[];
      publish_ready?: boolean;
      disclaimer?: string;
    };
  };
  paths?: Record<string, string>;
};

export async function fetchReadinessExportPack(opts?: {
  date?: string;
  include_tomorrow?: boolean;
}): Promise<ReadinessExportPack> {
  const { data } = await api.get("/api/marketing/operator/readiness-pack", {
    params: {
      ...(opts?.date ? { date: opts.date } : {}),
      ...(opts?.include_tomorrow === false ? { include_tomorrow: false } : {}),
    },
  });
  return data as ReadinessExportPack;
}

export async function downloadReadinessExportPack(
  format: "json" | "markdown" = "markdown",
  opts?: { date?: string; include_tomorrow?: boolean },
): Promise<void> {
  const { data } = await api.get("/api/marketing/operator/readiness-pack/export", {
    params: {
      format,
      ...(opts?.date ? { date: opts.date } : {}),
      ...(opts?.include_tomorrow === false ? { include_tomorrow: false } : {}),
    },
    responseType: "blob",
  });
  const ext = format === "markdown" ? "md" : "json";
  downloadBlob(data as Blob, `amos-readiness-pack.${ext}`);
}

export type ReadinessPackSnapshot = {
  ok: boolean;
  action: string;
  date?: string;
  version?: string;
  release?: string;
  host_ready: boolean;
  publish_ready: boolean;
  landing?: {
    page_count?: number;
    ready_count?: number;
    not_ready_count?: number;
  };
  calendar?: {
    date?: string;
    item_count?: number;
    ready?: boolean;
    failed_count?: number;
  };
  attention?: {
    count?: number;
    summary?: { high?: number; medium?: number; low?: number };
  };
  disclaimer?: string;
};

export async function snapshotReadinessExportPack(opts?: {
  date?: string;
  include_tomorrow?: boolean;
}): Promise<ReadinessPackSnapshot> {
  const { data } = await api.post("/api/marketing/operator/readiness-pack/snapshot", null, {
    params: {
      ...(opts?.date ? { date: opts.date } : {}),
      ...(opts?.include_tomorrow === false ? { include_tomorrow: false } : {}),
    },
  });
  return data as ReadinessPackSnapshot;
}

export type ReadinessPackHistoryItem = {
  id?: string;
  created_at?: string;
  date?: string;
  version?: string;
  release?: string;
  host_ready?: boolean;
  publish_ready?: boolean;
  landing_ready_count?: number;
  landing_page_count?: number;
  calendar_item_count?: number;
  attention_count?: number;
  attention_summary?: { high?: number; medium?: number; low?: number };
};

export type ReadinessPackHistory = {
  ok: boolean;
  count: number;
  items: ReadinessPackHistoryItem[];
  host_ready?: boolean;
  publish_ready?: boolean;
  path?: string;
  disclaimer?: string;
};

export async function fetchReadinessPackHistory(limit = 10): Promise<ReadinessPackHistory> {
  const { data } = await api.get("/api/marketing/operator/readiness-pack/history", {
    params: { limit },
  });
  return data as ReadinessPackHistory;
}

export type PlatformIntegrationService = {
  name?: string;
  role?: string;
  url_configured?: boolean;
  public_via_gateway?: string;
  probe?: {
    ok?: boolean;
    reachable?: boolean;
    reason?: string;
    status_code?: number;
  };
};

export type PlatformIntegration = {
  ok: boolean;
  consumer?: string;
  platform?: string;
  mode?: string;
  summary?: { probed?: number; healthy?: number };
  services?: Record<string, PlatformIntegrationService>;
  gateway?: {
    public_api_base?: string;
    marketing_via_gateway?: string;
    notifications_via_gateway?: string;
    auth_via_gateway?: string;
  };
  peers?: Record<
    string,
    { integration?: string; via_gateway?: string; public_api?: string }
  >;
  disclaimer?: string;
};

export async function fetchPlatformIntegration(): Promise<PlatformIntegration> {
  const { data } = await api.get("/api/marketing/platform/integration");
  return data as PlatformIntegration;
}

export type ReleaseReadinessCheck = {
  id: string;
  title: string;
  status: "pass" | "warn" | "fail" | string;
  detail?: string | null;
};

export type ReleaseReadiness = {
  ok: boolean;
  ready: boolean;
  version: string;
  release: string;
  summary: { pass: number; warn: number; fail: number; total: number };
  checks: ReleaseReadinessCheck[];
  env_catalog: Array<{ name: string; purpose: string }>;
  paths: Record<string, string>;
  constraints?: Record<string, boolean | string>;
  capabilities_count?: number;
};

export async function fetchReleaseReadiness(): Promise<ReleaseReadiness> {
  const { data } = await api.get("/api/marketing/release/readiness");
  return data as ReleaseReadiness;
}

export async function downloadReleaseReadinessExport(
  format: "json" | "markdown" = "json",
): Promise<void> {
  const { data } = await api.get("/api/marketing/release/readiness/export", {
    params: { format },
    responseType: "blob",
  });
  const ext = format === "markdown" ? "md" : "json";
  downloadBlob(data as Blob, `amos-readiness.${ext}`);
}

export type ReleaseReadinessSnapshot = {
  ok: boolean;
  action: string;
  ready: boolean;
  version?: string;
  release?: string;
  summary?: { pass?: number; warn?: number; fail?: number; total?: number };
};

export async function snapshotReleaseReadiness(): Promise<ReleaseReadinessSnapshot> {
  const { data } = await api.post("/api/marketing/release/readiness/snapshot");
  return data as ReleaseReadinessSnapshot;
}

export type SearchHit = {
  type: string;
  label: string;
  id: string;
  title: string;
  snippet: string;
  path: string;
  status?: string;
};

export type SearchFacet = {
  type: string;
  label: string;
  count: number;
};

export type SearchTypeOption = {
  type: string;
  label: string;
};

export type SearchResult = {
  ok: boolean;
  q: string;
  type?: string;
  items: SearchHit[];
  count: number;
  total_matches?: number;
  facets?: SearchFacet[];
  types?: SearchTypeOption[];
  message?: string;
};

export async function searchMarketing(
  q: string,
  limit = 40,
  type?: string,
): Promise<SearchResult> {
  const { data } = await api.get("/api/marketing/search", {
    params: { q, limit, ...(type ? { type } : {}) },
  });
  return data as SearchResult;
}

export type ImportCatalog = {
  items: Array<{ id: string; label: string; default: boolean; notes?: string | null }>;
  modes: string[];
  defaults: { skip: string[]; mode: string };
};

export type ImportBundleResult = {
  ok: boolean;
  tenant_id: string;
  mode: string;
  dry_run: boolean;
  results: Record<
    string,
    {
      created?: number;
      skipped?: number;
      error?: string;
      skipped_dataset?: boolean;
      reason?: string;
      error_count?: number;
      errors?: string[];
    }
  >;
  totals: { created: number; skipped: number; errors: number };
  constraints?: Record<string, boolean>;
};

export async function fetchImportCatalog(): Promise<ImportCatalog> {
  const { data } = await api.get("/api/marketing/import/catalog");
  return data as ImportCatalog;
}

export async function importExportBundle(input: {
  bundle: Record<string, unknown>;
  mode?: "merge" | "create_as_new";
  dry_run?: boolean;
  include_skipped?: boolean;
}): Promise<ImportBundleResult> {
  const { data } = await api.post("/api/marketing/import/bundle", {
    bundle: input.bundle,
    mode: input.mode || "merge",
    dry_run: Boolean(input.dry_run),
    include_skipped: Boolean(input.include_skipped),
  });
  return data as ImportBundleResult;
}

export type ExportCatalogItem = {
  id: string;
  label: string;
  formats: string[];
};

export async function fetchExportCatalog(): Promise<ExportCatalogItem[]> {
  const { data } = await api.get("/api/marketing/export/catalog");
  return (data?.items || []) as ExportCatalogItem[];
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadExportDataset(
  dataset: string,
  format: "json" | "csv" = "json",
): Promise<void> {
  const { data } = await api.get(`/api/marketing/export/${dataset}`, {
    params: { format },
    responseType: "blob",
  });
  downloadBlob(data as Blob, `amos-${dataset}.${format}`);
}

export async function downloadExportBundle(datasets?: string[]): Promise<void> {
  const { data } = await api.get("/api/marketing/export/bundle", {
    params: datasets?.length ? { datasets: datasets.join(",") } : {},
    responseType: "blob",
  });
  downloadBlob(data as Blob, "amos-export-bundle.json");
}

export type AlertsStatus = {
  enabled: boolean;
  notification_service_url: boolean;
  global_webhook_configured: boolean;
  digest?: { backend: string; enabled: boolean };
  tenant: {
    enabled: boolean;
    has_email: boolean;
    has_user_id: boolean;
    has_webhook: boolean;
    channels: string[];
    quiet_hours?: {
      enabled: boolean;
      start: string;
      end: string;
      timezone: string;
      strict: boolean;
    };
    quiet_hours_active?: boolean;
    buffered_count?: number;
  };
};

export async function fetchAlertsStatus(): Promise<AlertsStatus> {
  const { data } = await api.get("/api/marketing/alerts/status");
  return data as AlertsStatus;
}

export async function sendTestAlert(): Promise<Record<string, unknown>> {
  const { data } = await api.post("/api/marketing/alerts/test");
  return data as Record<string, unknown>;
}

export type InboxNotification = {
  id: string;
  event_type: string;
  title: string;
  body: string;
  priority: string;
  path?: string | null;
  read_at?: string | null;
  dismissed_at?: string | null;
  created_at?: string;
  meta?: Record<string, unknown>;
};

export type InboxList = {
  ok: boolean;
  items: InboxNotification[];
  unread_count: number;
  total: number;
};

export async function fetchInboxNotifications(opts?: {
  unread?: boolean;
  limit?: number;
}): Promise<InboxList> {
  const { data } = await api.get("/api/marketing/notifications", {
    params: { unread: opts?.unread || undefined, limit: opts?.limit },
  });
  return data as InboxList;
}

export async function fetchInboxUnreadCount(): Promise<number> {
  const { data } = await api.get("/api/marketing/notifications/unread-count");
  return Number(data?.unread_count || 0);
}

export async function markInboxNotificationRead(id: string): Promise<InboxNotification> {
  const { data } = await api.post(`/api/marketing/notifications/${id}/read`);
  return data.notification as InboxNotification;
}

export async function markAllInboxNotificationsRead(): Promise<{ marked: number; unread_count: number }> {
  const { data } = await api.post("/api/marketing/notifications/read-all");
  return data as { marked: number; unread_count: number };
}

export async function dismissInboxNotification(id: string): Promise<InboxNotification> {
  const { data } = await api.post(`/api/marketing/notifications/${id}/dismiss`);
  return data.notification as InboxNotification;
}

export type DigestSnapshot = {
  tenant_id: string;
  quiet_hours_active: boolean;
  buffered_count: number;
  items: Array<Record<string, unknown>>;
  quiet_hours: Record<string, unknown>;
};

export async function fetchAlertDigest(): Promise<DigestSnapshot> {
  const { data } = await api.get("/api/marketing/alerts/digest");
  return data as DigestSnapshot;
}

export async function flushAlertDigest(): Promise<Record<string, unknown>> {
  const { data } = await api.post("/api/marketing/alerts/digest/flush");
  return data as Record<string, unknown>;
}

export type DigestSchedulerStatus = {
  enabled: boolean;
  interval_seconds: number;
  last_tick?: Record<string, unknown> | null;
};

export async function fetchDigestSchedulerStatus(): Promise<DigestSchedulerStatus> {
  const { data } = await api.get("/api/marketing/alerts/digest/scheduler");
  return data as DigestSchedulerStatus;
}

export type KpiDigestPrefs = {
  enabled: boolean;
  cadence: "daily" | "weekly" | string;
  hour_utc: number;
  weekday: number;
  last_sent_at?: string | null;
  timezone?: string;
};

export type KpiDigestSnapshot = {
  ok: boolean;
  tenant_id: string;
  prefs: KpiDigestPrefs;
  kpis: Array<{ id: string; label: string; value: string; delta: string; trend: string }>;
  summary: string;
  generated_at: string;
  history_count: number;
};

export async function fetchKpiDigest(): Promise<KpiDigestSnapshot> {
  const { data } = await api.get("/api/marketing/kpi-digest");
  return data as KpiDigestSnapshot;
}

export async function saveKpiDigestPrefs(input: {
  enabled?: boolean;
  cadence?: "daily" | "weekly";
  hour_utc?: number;
  weekday?: number;
}): Promise<{ ok: boolean; prefs: KpiDigestPrefs }> {
  const { data } = await api.put("/api/marketing/kpi-digest/prefs", input);
  return data as { ok: boolean; prefs: KpiDigestPrefs };
}

export async function sendKpiDigest(opts?: {
  force?: boolean;
  dry_run?: boolean;
}): Promise<Record<string, unknown>> {
  const { data } = await api.post("/api/marketing/kpi-digest/send", null, {
    params: {
      force: opts?.force !== false,
      dry_run: Boolean(opts?.dry_run),
    },
  });
  return data as Record<string, unknown>;
}

export async function fetchKpiDigestHistory(limit = 10): Promise<{
  ok: boolean;
  items: Array<{
    id?: string;
    sent_at?: string;
    cadence?: string;
    summary?: string | null;
    dry_run?: boolean;
    forced?: boolean | null;
    publish_ok?: boolean | null;
    source?: string;
  }>;
  count: number;
  counts?: { forced?: number; dry_run?: number };
  prefs?: KpiDigestPrefs;
  path?: string;
  weekday_labels?: string[];
}> {
  const { data } = await api.get("/api/marketing/kpi-digest/history", {
    params: { limit },
  });
  return data as {
    ok: boolean;
    items: Array<{
      id?: string;
      sent_at?: string;
      cadence?: string;
      summary?: string | null;
      dry_run?: boolean;
      forced?: boolean | null;
      publish_ok?: boolean | null;
      source?: string;
    }>;
    count: number;
    counts?: { forced?: number; dry_run?: number };
    prefs?: KpiDigestPrefs;
    path?: string;
    weekday_labels?: string[];
  };
}

export async function runDigestSchedulerTick(dryRun = false): Promise<Record<string, unknown>> {
  const { data } = await api.post("/api/marketing/alerts/digest/scheduler/tick", null, {
    params: { dry_run: dryRun },
  });
  return data as Record<string, unknown>;
}

export async function fetchHelpArticles(): Promise<HelpArticle[]> {
  const { data } = await api.get("/api/marketing/help/articles");
  return (data?.items || []) as HelpArticle[];
}

export type OAuthSetupItem = {
  provider: string;
  label: string;
  family?: string;
  configured: boolean;
  live_ready?: boolean;
  mode?: "live" | "sandbox";
  sandbox_available: boolean;
  pkce?: boolean;
  redirect_uri: string;
  scopes: string[];
  console_url: string;
  help: string;
  env?: {
    client_id: string;
    client_secret: string;
    client_id_set: boolean;
    client_secret_set: boolean;
    client_id_hint?: string;
  };
  checklist?: Array<{ id: string; ok: boolean; env?: string; value?: string; optional?: boolean }>;
};

export type OAuthLiveReadiness = {
  ok: boolean;
  sandbox_allowed: boolean;
  require_live: boolean;
  public_api_base: string;
  frontend_url: string;
  live_ready_count: number;
  total_providers: number;
  capability: string;
  message: string;
  families: Record<
    string,
    {
      configured: boolean;
      client_id_set: boolean;
      client_secret_set: boolean;
      client_id_hint?: string;
      env: { client_id: string; client_secret: string };
    }
  >;
  providers: OAuthSetupItem[];
};

export type OAuthAuthorizeResult = {
  provider: string;
  mode: "oauth" | "sandbox";
  configured: boolean;
  live_ready?: boolean;
  state: string;
  authorize_url: string | null;
  sandbox_connect_url?: string;
  message?: string;
  redirect_uri: string;
  pkce?: boolean;
};

export type OAuthPreflight = {
  ok: boolean;
  provider: string;
  live_ready: boolean;
  mode: string;
  redirect_uri: string;
  scopes: string[];
  pkce: boolean;
  issues: string[];
  console_url: string;
  next_step: string;
};

export type MarketingPermissions = {
  role: string;
  permissions: string[];
  enforce: boolean;
};

export async function fetchOAuthSetup(): Promise<{
  items: OAuthSetupItem[];
  live?: {
    live_ready_count: number;
    total_providers: number;
    sandbox_allowed: boolean;
    require_live: boolean;
    families: OAuthLiveReadiness["families"];
    message: string;
  };
  frontend_url?: string;
}> {
  const { data } = await api.get("/api/marketing/integrations/oauth/setup");
  return data as {
    items: OAuthSetupItem[];
    live?: {
      live_ready_count: number;
      total_providers: number;
      sandbox_allowed: boolean;
      require_live: boolean;
      families: OAuthLiveReadiness["families"];
      message: string;
    };
    frontend_url?: string;
  };
}

export async function fetchOAuthLiveReadiness(): Promise<OAuthLiveReadiness> {
  const { data } = await api.get("/api/marketing/integrations/oauth/live-readiness");
  return data as OAuthLiveReadiness;
}

export async function fetchOAuthPreflight(provider: string): Promise<OAuthPreflight> {
  const { data } = await api.get(`/api/marketing/integrations/oauth/${provider}/preflight`);
  return data as OAuthPreflight;
}

export async function startOAuth(provider: string): Promise<OAuthAuthorizeResult> {
  const { data } = await api.get(`/api/marketing/integrations/oauth/${provider}/authorize`, {
    params: { return_to: window.location.origin },
  });
  return data as OAuthAuthorizeResult;
}

export async function sandboxConnect(provider: string): Promise<Integration> {
  const { data } = await api.post(`/api/marketing/integrations/oauth/${provider}/sandbox`);
  return data as Integration;
}

/* ---------------------------------------------------------------------------
   YouTube connector (Data API v3 + Analytics API)
   ---------------------------------------------------------------------------
   The connector has its own surface because it is much deeper than a single
   "connected / not connected" card: channel identity, analytics (including
   Shorts), video management, comments, uploads and the AI assistant.
   ------------------------------------------------------------------------- */

export type YouTubeQuotaBreakdown = {
  videos_shorts?: number;
  analytics?: number;
  comments_moderation?: number;
  live?: number;
  playlists?: number;
  captions?: number;
  publishing?: number;
  other?: number;
};

export type YouTubeQuota = {
  used: number;
  limit: number;
  remaining: number;
  exhausted: boolean;
  resets_at?: string;
  breakdown?: YouTubeQuotaBreakdown;
  updated_at?: string;
  timezone?: string;
  source?: string;
};

export type YouTubeChannel = {
  channel_id: string;
  title: string;
  handle: string;
  url: string;
  avatar_url: string;
  subscribers: number;
  views: number;
  videos: number;
  hidden_subscribers?: boolean;
  last_sync_at?: string;
  verify_error?: string;
};

/**
 * The feature keys the backend actually publishes in `features.granted`
 * (`app/youtube_service.py` → `FEATURES`). Declared as a union so that reading a key the
 * backend never sends is a **compile error** instead of a check that is silently always
 * false — which is exactly how the moderation permission warning used to be wrong.
 *
 * `youtube.force-ssl` is what unlocks comment moderation, replies, video editing and
 * uploads; the backend reports that single consent under `comments`, `manage_videos`
 * and `upload`.
 */
export type YouTubeFeatureKey =
  | "read"
  | "analytics"
  | "shorts_analytics"
  | "manage_videos"
  | "comments"
  | "upload";

export type YouTubeFeatures = {
  /** Absent keys mean "not granted" — the backend omits nothing, but stays partial. */
  granted: Partial<Record<YouTubeFeatureKey, boolean>>;
  missing: string[];
  scopes: string[];
  needs_reconnect: boolean;
  reconnect_hint: string;
};

export type YouTubeConnection = {
  ok: boolean;
  provider: string;
  connected: boolean;
  mode: string;
  live: boolean;
  channel: Partial<YouTubeChannel>;
  features: YouTubeFeatures;
  quota: YouTubeQuota;
  last_sync_at?: string;
  recent_uploads?: YouTubeUpload[];
};

export type YouTubeRange = { start: string; end: string; label?: string };

export type YouTubeChannelReport = {
  ok: boolean;
  range: YouTubeRange;
  metrics: string[];
  totals: Record<string, number | null>;
  series: Record<string, number | string | null>[];
  reason?: string;
  detail?: string;
};

export type YouTubeTopVideo = {
  video_id: string;
  title?: string;
  thumbnail_url?: string;
  url?: string;
  is_short?: boolean;
  published_at?: string;
  duration_seconds?: number | null;
  views?: number | null;
  watch_time_minutes?: number | null;
  likes?: number | null;
  comments?: number | null;
  shares?: number | null;
};

export type YouTubeShortsReport = {
  ok: boolean;
  classification: "creatorContentType" | "duration";
  classification_note?: string;
  /** How the per-video Shorts list was identified (always the duration rule). */
  top_shorts_method?: "duration";
  top_shorts_note?: string;
  /** Set when the per-video list could not be fetched; the totals stay valid. */
  top_shorts_reason?: string;
  range: YouTubeRange;
  metrics: string[];
  shorts: Record<string, number | null> & { count?: number; videos?: YouTubeTopVideo[] };
  long_form: Record<string, number | null> & { count?: number };
  comparison: { metric: string; shorts: number | null; long_form: number | null; ratio: number | null }[];
  top_shorts: YouTubeTopVideo[];
  reason?: string;
  detail?: string;
};

export type YouTubeVideo = {
  video_id: string;
  title: string;
  description: string;
  tags: string[];
  category_id: string;
  published_at: string;
  thumbnail_url: string;
  duration_seconds: number;
  is_short: boolean;
  privacy_status: string;
  upload_status?: string;
  views: number;
  likes: number;
  comments: number;
  url: string;
  shorts_url?: string;
  performance?: Record<string, number | null>;
};

export type YouTubeComment = {
  comment_id: string;
  parent_id?: string;
  author: string;
  author_avatar_url?: string;
  text: string;
  published_at: string;
  like_count: number;
  by_channel?: boolean;
};

export type YouTubeCommentThread = {
  thread_id: string;
  video_id: string;
  comment: YouTubeComment;
  replies: YouTubeComment[];
  reply_count: number;
  answered: boolean;
  video_title?: string;
  video_thumbnail_url?: string;
  video_url?: string;
};

export type YouTubeUpload = {
  upload_id: string;
  status: string;
  title: string;
  privacy_status: string;
  publish_at?: string;
  is_short?: boolean;
  progress_percent: number;
  bytes_total?: number;
  bytes_sent?: number;
  video_id?: string;
  video_url?: string;
  shorts_url?: string;
  thumbnail_set?: boolean;
  warning?: string;
  error?: string;
  detail?: string;
  youtube_status?: string;
  created_at?: string;
  updated_at?: string;
};

export type YouTubeAiSuggestion = {
  text: string;
  why?: string;
  score?: number;
  title?: string;
  description?: string;
  tags?: string;
};

export type YouTubeAiResult = {
  ok: boolean;
  reason?: string;
  detail?: string;
  model?: string;
  count?: number;
  suggestions: YouTubeAiSuggestion[];
};

export type YouTubeAnalyticsParams = {
  preset?: string;
  start?: string;
  end?: string;
  days?: number;
};

export async function fetchYouTubeConnection(): Promise<YouTubeConnection> {
  const { data } = await api.get("/api/marketing/youtube/connection");
  return data as YouTubeConnection;
}

export async function fetchYouTubeQuota(): Promise<{ quota: YouTubeQuota; costs: Record<string, number> }> {
  const { data } = await api.get("/api/marketing/youtube/quota");
  return data as { quota: YouTubeQuota; costs: Record<string, number> };
}

export async function syncYouTube(body?: {
  days?: number;
  with_analytics?: boolean;
  with_videos?: boolean;
}): Promise<Record<string, unknown>> {
  const { data } = await api.post("/api/marketing/youtube/sync", body ?? {});
  return data as Record<string, unknown>;
}

export async function disconnectYouTube(): Promise<Record<string, unknown>> {
  const { data } = await api.post("/api/marketing/youtube/disconnect");
  return data as Record<string, unknown>;
}

export async function fetchYouTubeAnalytics(
  params: YouTubeAnalyticsParams = {},
): Promise<{
  channel: YouTubeChannelReport;
  shorts?: YouTubeShortsReport;
  top_videos?: { items: YouTubeTopVideo[] };
}> {
  const { data } = await api.get("/api/marketing/youtube/analytics/overview", { params });
  return data as {
    channel: YouTubeChannelReport;
    shorts?: YouTubeShortsReport;
    top_videos?: { items: YouTubeTopVideo[] };
  };
}

export async function fetchYouTubeChannelAnalytics(
  params: YouTubeAnalyticsParams & { metrics?: string } = {},
): Promise<YouTubeChannelReport> {
  const { data } = await api.get("/api/marketing/youtube/analytics/channel", { params });
  return data as YouTubeChannelReport;
}

export async function fetchYouTubeTopVideos(
  params: YouTubeAnalyticsParams & { limit?: number; content_type?: string } = {},
): Promise<{ items: YouTubeTopVideo[] }> {
  const { data } = await api.get("/api/marketing/youtube/analytics/videos", { params });
  return data as { items: YouTubeTopVideo[] };
}

export async function fetchYouTubeShortsAnalytics(
  params: YouTubeAnalyticsParams & { limit?: number } = {},
): Promise<YouTubeShortsReport> {
  const { data } = await api.get("/api/marketing/youtube/analytics/shorts", { params });
  return data as YouTubeShortsReport;
}

export async function fetchYouTubeVideoAnalytics(
  videoId: string,
  params: YouTubeAnalyticsParams = {},
): Promise<{ video_id: string; totals: Record<string, number | null>; range: YouTubeRange }> {
  const { data } = await api.get(`/api/marketing/youtube/analytics/video/${videoId}`, { params });
  return data as { video_id: string; totals: Record<string, number | null>; range: YouTubeRange };
}

export async function fetchYouTubeVideos(
  params: {
    query?: string;
    sort?: string;
    max_results?: number;
    page_token?: string;
    published_after?: string;
    published_before?: string;
    privacy?: string;
    shorts_only?: string;
    /** Server-side Videos/Shorts separation: all | video | short | unclassified. */
    kind?: "all" | "video" | "short" | "unclassified";
    with_performance?: boolean;
    performance_days?: number;
  } = {},
): Promise<YouTubeLibraryPage> {
  const { data } = await api.get("/api/marketing/youtube/videos", { params });
  return data as YouTubeLibraryPage;
}

export async function fetchYouTubeVideo(
  videoId: string,
  params: { with_performance?: boolean; performance_days?: number } = {},
): Promise<{
  video: YouTubeVideo;
  performance?: { totals: Record<string, number | null> };
  range?: YouTubeRange;
}> {
  const { data } = await api.get(`/api/marketing/youtube/videos/${videoId}`, { params });
  return data as {
    video: YouTubeVideo;
    performance?: { totals: Record<string, number | null> };
    range?: YouTubeRange;
  };
}

export async function updateYouTubeVideo(
  videoId: string,
  body: {
    title?: string;
    description?: string;
    tags?: string[];
    category_id?: string;
    privacy_status?: "public" | "unlisted" | "private";
    made_for_kids?: boolean;
  },
): Promise<{ video: YouTubeVideo; changed: string[] }> {
  const { data } = await api.patch(`/api/marketing/youtube/videos/${videoId}`, body);
  return data as { video: YouTubeVideo; changed: string[] };
}

export async function setYouTubeThumbnail(
  videoId: string,
  body: { source_url?: string; media_upload_id?: string },
): Promise<{ thumbnail_urls: Record<string, string>; video?: YouTubeVideo }> {
  const { data } = await api.post(`/api/marketing/youtube/videos/${videoId}/thumbnail`, body);
  return data as { thumbnail_urls: Record<string, string>; video?: YouTubeVideo };
}

export async function setYouTubeVisibility(
  videoId: string,
  privacyStatus: "public" | "unlisted" | "private",
): Promise<{ video: YouTubeVideo }> {
  const { data } = await api.post(`/api/marketing/youtube/videos/${videoId}/visibility`, {
    privacy_status: privacyStatus,
  });
  return data as { video: YouTubeVideo };
}

export async function fetchYouTubeComments(
  params: {
    video_id?: string;
    unanswered_only?: boolean;
    max_results?: number;
    page_token?: string;
    order?: string;
  } = {},
): Promise<{
  items: YouTubeCommentThread[];
  count: number;
  unanswered_count: number;
  answered_count: number;
  next_page_token: string;
}> {
  const { data } = await api.get("/api/marketing/youtube/comments", { params });
  return data as {
    items: YouTubeCommentThread[];
    count: number;
    unanswered_count: number;
    answered_count: number;
    next_page_token: string;
  };
}

export async function fetchYouTubeCommentsInbox(
  params: { unanswered_only?: boolean; max_results?: number; page_token?: string } = {},
): Promise<{
  items: YouTubeCommentThread[];
  count: number;
  unanswered_count: number;
  answered_count: number;
  next_page_token?: string;
}> {
  const { data } = await api.get("/api/marketing/youtube/comments/inbox", { params });
  return data as {
    items: YouTubeCommentThread[];
    count: number;
    unanswered_count: number;
    answered_count: number;
    next_page_token?: string;
  };
}

export async function replyToYouTubeComment(
  parentId: string,
  text: string,
): Promise<{ reply: YouTubeComment; comment_id: string }> {
  const { data } = await api.post("/api/marketing/youtube/comments/reply", {
    parent_id: parentId,
    text,
  });
  return data as { reply: YouTubeComment; comment_id: string };
}

export async function suggestYouTubeReply(body: {
  comment_id?: string;
  comment?: string;
  video_id?: string;
  count?: number;
}): Promise<YouTubeAiResult> {
  const { data } = await api.post("/api/marketing/youtube/comments/ai-reply", body);
  return data as YouTubeAiResult;
}

export async function uploadYouTubeVideo(body: {
  title: string;
  description?: string;
  tags?: string[];
  category_id?: string;
  privacy_status?: "public" | "unlisted" | "private";
  made_for_kids?: boolean;
  publish_at?: string;
  source_url?: string;
  media_upload_id?: string;
  thumbnail_url?: string;
  thumbnail_media_id?: string;
  is_short?: boolean;
}): Promise<{ upload: YouTubeUpload }> {
  const { data } = await api.post("/api/marketing/youtube/uploads", body);
  return data as { upload: YouTubeUpload };
}

export async function fetchYouTubeUploads(
  params: { limit?: number } = {},
): Promise<{ items: YouTubeUpload[]; count: number }> {
  const { data } = await api.get("/api/marketing/youtube/uploads", { params });
  return data as { items: YouTubeUpload[]; count: number };
}

export async function fetchYouTubeUpload(uploadId: string): Promise<{ upload: YouTubeUpload }> {
  const { data } = await api.get(`/api/marketing/youtube/uploads/${uploadId}`);
  return data as { upload: YouTubeUpload };
}

export async function refreshYouTubeUpload(uploadId: string): Promise<{ upload: YouTubeUpload }> {
  const { data } = await api.post(`/api/marketing/youtube/uploads/${uploadId}/refresh`);
  return data as { upload: YouTubeUpload };
}

export async function suggestYouTubeAi(body: {
  action: "titles" | "descriptions" | "tags" | "ideas" | "shorts_script" | "metadata";
  topic?: string;
  channel?: string;
  audience?: string;
  goal?: string;
  transcript?: string;
  notes?: string;
  count?: number;
}): Promise<YouTubeAiResult> {
  const { data } = await api.post("/api/marketing/youtube/ai/suggest", body);
  return data as YouTubeAiResult;
}

/* ---------------------------------------------------------------------------
   YouTube Studio additions (live, playlists, captions, moderation, library)
   ---------------------------------------------------------------------------
   These call the additive `/api/marketing/youtube/*` studio routes. Every one
   of them is tenant-scoped by the signed-in session, so no tenant id is sent
   from the browser.
   ------------------------------------------------------------------------- */

export type YouTubeClassificationTotals = {
  shorts: number;
  videos: number;
  unclassified: number;
  measured: number;
  complete: boolean;
};

export type YouTubeClassification = {
  ok: boolean;
  totals: YouTubeClassificationTotals;
  min_upload_date: string;
  max_short_seconds: number;
  quota: YouTubeQuota;
};

export type YouTubeLibraryItem = YouTubeVideo & {
  classification?: "short" | "video" | "unclassified";
  classification_reason?: string;
  width?: number | null;
  height?: number | null;
  caption_available?: boolean;
};

export type YouTubeLibraryPage = {
  ok: boolean;
  items: YouTubeLibraryItem[];
  count: number;
  total?: number;
  next_page_token: string;
  has_more?: boolean;
  classification?: YouTubeClassificationTotals;
  totals?: YouTubeClassificationTotals;
  quota?: YouTubeQuota;
  note?: string;
};

export type YouTubeLibraryParams = {
  query?: string;
  sort?: string;
  max_results?: number;
  page_token?: string;
  published_after?: string;
  published_before?: string;
  privacy?: string;
  with_performance?: boolean;
  performance_days?: number;
};

export async function fetchYouTubeShortsLibrary(
  params: YouTubeLibraryParams = {},
): Promise<YouTubeLibraryPage> {
  const { data } = await api.get("/api/marketing/youtube/shorts", { params });
  return data as YouTubeLibraryPage;
}

export async function fetchYouTubeClassification(): Promise<YouTubeClassification> {
  const { data } = await api.get("/api/marketing/youtube/videos/classification");
  return data as YouTubeClassification;
}

export async function refreshYouTubeClassification(
  maxPages = 10,
): Promise<{ ok: boolean; totals?: YouTubeClassificationTotals; quota?: YouTubeQuota }> {
  const { data } = await api.post("/api/marketing/youtube/videos/classification/refresh", null, {
    params: { max_pages: maxPages },
  });
  return data as { ok: boolean; totals?: YouTubeClassificationTotals; quota?: YouTubeQuota };
}

/* ---------------------------------------------------------------------------
   Live streaming
   ---------------------------------------------------------------------------
   Broadcasts (the scheduled/live event) and streams (the reusable encoder
   target) are separate resources on YouTube, so they are separate here too.
   Stream keys are never returned by the API — the screen links to YouTube
   Studio for the encoder key.
   ------------------------------------------------------------------------- */

export type YouTubeBroadcast = {
  broadcast_id: string;
  title: string;
  description: string;
  lifecycle_status: string;
  privacy_status: string;
  scheduled_start: string;
  scheduled_end: string;
  actual_start: string;
  actual_end: string;
  bound_stream_id: string;
  is_default_broadcast: boolean;
  monitor_stream: boolean;
  enable_auto_start?: boolean | null;
  enable_auto_stop?: boolean | null;
  enable_embed?: boolean | null;
  made_for_kids?: boolean | null;
  thumbnail_url: string;
  watch_url: string;
};

export type YouTubeLiveStream = {
  stream_id: string;
  title: string;
  description: string;
  resolution: string;
  frame_rate: string;
  ingestion_type: string;
  ingest_address: string;
  backup_ingest_address: string;
  stream_status: string;
  health_status: string;
  configuration_issues: string[];
  /** Always false: the encoder key is never sent to the browser. */
  stream_key_exposed: boolean;
  setup_steps?: string;
};

export type YouTubeBroadcastList = {
  ok: boolean;
  items: YouTubeBroadcast[];
  count: number;
  state: string;
  query: string;
  next_page_token: string;
  quota?: YouTubeQuota;
};

export async function fetchYouTubeBroadcasts(
  params: { state?: string; query?: string; max_results?: number; page_token?: string } = {},
): Promise<YouTubeBroadcastList> {
  const { data } = await api.get("/api/marketing/youtube/live/broadcasts", { params });
  return data as YouTubeBroadcastList;
}

export async function createYouTubeBroadcast(body: {
  title: string;
  description?: string;
  scheduled_start: string;
  scheduled_end?: string;
  privacy?: "public" | "unlisted" | "private";
  made_for_kids?: boolean;
  latency?: string;
}): Promise<{ ok: boolean; broadcast: YouTubeBroadcast; next_step?: string; quota?: YouTubeQuota }> {
  const { data } = await api.post("/api/marketing/youtube/live/broadcasts", body);
  return data as {
    ok: boolean;
    broadcast: YouTubeBroadcast;
    next_step?: string;
    quota?: YouTubeQuota;
  };
}

export async function fetchYouTubeBroadcast(
  broadcastId: string,
): Promise<{ ok: boolean; broadcast: YouTubeBroadcast; quota?: YouTubeQuota }> {
  const { data } = await api.get(`/api/marketing/youtube/live/broadcasts/${broadcastId}`);
  return data as { ok: boolean; broadcast: YouTubeBroadcast; quota?: YouTubeQuota };
}

export async function updateYouTubeBroadcast(
  broadcastId: string,
  body: {
    title?: string;
    description?: string;
    scheduled_start?: string;
    scheduled_end?: string;
    privacy?: "public" | "unlisted" | "private";
  },
): Promise<{ ok: boolean; broadcast: YouTubeBroadcast; quota?: YouTubeQuota }> {
  const { data } = await api.patch(`/api/marketing/youtube/live/broadcasts/${broadcastId}`, body);
  return data as { ok: boolean; broadcast: YouTubeBroadcast; quota?: YouTubeQuota };
}

export async function deleteYouTubeBroadcast(
  broadcastId: string,
): Promise<{ ok: boolean; deleted: boolean; broadcast_id: string }> {
  const { data } = await api.delete(`/api/marketing/youtube/live/broadcasts/${broadcastId}`);
  return data as { ok: boolean; deleted: boolean; broadcast_id: string };
}

export async function bindYouTubeBroadcast(
  broadcastId: string,
  streamId: string,
): Promise<{ ok: boolean; broadcast: YouTubeBroadcast; quota?: YouTubeQuota }> {
  const { data } = await api.post(`/api/marketing/youtube/live/broadcasts/${broadcastId}/bind`, {
    stream_id: streamId,
  });
  return data as { ok: boolean; broadcast: YouTubeBroadcast; quota?: YouTubeQuota };
}

export async function transitionYouTubeBroadcast(
  broadcastId: string,
  status: "testing" | "live" | "complete",
): Promise<{ ok: boolean; broadcast: YouTubeBroadcast; quota?: YouTubeQuota }> {
  const { data } = await api.post(
    `/api/marketing/youtube/live/broadcasts/${broadcastId}/transition`,
    { status },
  );
  return data as { ok: boolean; broadcast: YouTubeBroadcast; quota?: YouTubeQuota };
}

export type YouTubeStreamList = {
  ok: boolean;
  items: YouTubeLiveStream[];
  count: number;
  next_page_token: string;
  setup_steps?: string;
  quota?: YouTubeQuota;
};

export async function fetchYouTubeStreams(
  params: { max_results?: number; page_token?: string } = {},
): Promise<YouTubeStreamList> {
  const { data } = await api.get("/api/marketing/youtube/live/streams", { params });
  return data as YouTubeStreamList;
}

export async function createYouTubeStream(body: {
  title: string;
  description?: string;
  resolution?: string;
  frame_rate?: string;
  ingestion_type?: string;
}): Promise<{
  ok: boolean;
  stream: YouTubeLiveStream;
  stream_key_note?: string;
  quota?: YouTubeQuota;
}> {
  const { data } = await api.post("/api/marketing/youtube/live/streams", body);
  return data as {
    ok: boolean;
    stream: YouTubeLiveStream;
    stream_key_note?: string;
    quota?: YouTubeQuota;
  };
}

export async function updateYouTubeStream(
  streamId: string,
  body: { title?: string; description?: string; resolution?: string; frame_rate?: string },
): Promise<{ ok: boolean; stream: YouTubeLiveStream; quota?: YouTubeQuota }> {
  const { data } = await api.patch(`/api/marketing/youtube/live/streams/${streamId}`, body);
  return data as { ok: boolean; stream: YouTubeLiveStream; quota?: YouTubeQuota };
}

export async function deleteYouTubeStream(
  streamId: string,
): Promise<{ ok: boolean; deleted: boolean; stream_id: string }> {
  const { data } = await api.delete(`/api/marketing/youtube/live/streams/${streamId}`);
  return data as { ok: boolean; deleted: boolean; stream_id: string };
}

/* ---------------------------------------------------------------------------
   Playlists
   ---------------------------------------------------------------------------
   Deleting or editing a playlist never deletes the videos in it — the API only
   removes the playlist entry, which is what the screen promises.
   ------------------------------------------------------------------------- */

export type YouTubePlaylist = {
  playlist_id: string;
  title: string;
  description: string;
  privacy_status: string;
  item_count: number;
  published_at: string;
  channel_id: string;
  channel_title: string;
  thumbnail_url: string;
  url: string;
};

export type YouTubePlaylistItem = {
  playlist_item_id: string;
  video_id: string;
  title: string;
  description: string;
  position: number;
  published_at: string;
  channel_title: string;
  thumbnail_url: string;
  privacy_status?: string;
  duration_seconds?: number;
  views?: number;
  classification?: string;
};

export async function fetchYouTubePlaylists(
  params: { max_results?: number; page_token?: string } = {},
): Promise<{ ok: boolean; items: YouTubePlaylist[]; count: number; next_page_token: string; quota?: YouTubeQuota }> {
  const { data } = await api.get("/api/marketing/youtube/playlists", { params });
  return data as {
    ok: boolean;
    items: YouTubePlaylist[];
    count: number;
    next_page_token: string;
    quota?: YouTubeQuota;
  };
}

export async function fetchYouTubePlaylist(
  playlistId: string,
): Promise<{ ok: boolean; playlist: YouTubePlaylist; quota?: YouTubeQuota }> {
  const { data } = await api.get(`/api/marketing/youtube/playlists/${playlistId}`);
  return data as { ok: boolean; playlist: YouTubePlaylist; quota?: YouTubeQuota };
}

export async function createYouTubePlaylist(body: {
  title: string;
  description?: string;
  privacy?: "public" | "unlisted" | "private";
}): Promise<{ ok: boolean; playlist: YouTubePlaylist; quota?: YouTubeQuota }> {
  const { data } = await api.post("/api/marketing/youtube/playlists", body);
  return data as { ok: boolean; playlist: YouTubePlaylist; quota?: YouTubeQuota };
}

export async function updateYouTubePlaylist(
  playlistId: string,
  body: {
    title?: string;
    description?: string;
    privacy?: "public" | "unlisted" | "private";
  },
): Promise<{ ok: boolean; playlist: YouTubePlaylist; quota?: YouTubeQuota }> {
  const { data } = await api.patch(`/api/marketing/youtube/playlists/${playlistId}`, body);
  return data as { ok: boolean; playlist: YouTubePlaylist; quota?: YouTubeQuota };
}

export async function deleteYouTubePlaylist(
  playlistId: string,
): Promise<{ ok: boolean; deleted: boolean; playlist_id: string; note?: string }> {
  const { data } = await api.delete(`/api/marketing/youtube/playlists/${playlistId}`);
  return data as { ok: boolean; deleted: boolean; playlist_id: string; note?: string };
}

export async function fetchYouTubePlaylistItems(
  playlistId: string,
  params: { max_results?: number; page_token?: string } = {},
): Promise<{
  ok: boolean;
  items: YouTubePlaylistItem[];
  count: number;
  next_page_token: string;
  quota?: YouTubeQuota;
}> {
  const { data } = await api.get(`/api/marketing/youtube/playlists/${playlistId}/items`, { params });
  return data as {
    ok: boolean;
    items: YouTubePlaylistItem[];
    count: number;
    next_page_token: string;
    quota?: YouTubeQuota;
  };
}

export async function addYouTubePlaylistItem(
  playlistId: string,
  body: { video_id: string; position?: number },
): Promise<{ ok: boolean; item: YouTubePlaylistItem; quota?: YouTubeQuota }> {
  const { data } = await api.post(`/api/marketing/youtube/playlists/${playlistId}/items`, body);
  return data as { ok: boolean; item: YouTubePlaylistItem; quota?: YouTubeQuota };
}

export async function removeYouTubePlaylistItem(
  playlistId: string,
  itemId: string,
): Promise<{ ok: boolean; deleted: boolean; playlist_item_id: string; note?: string }> {
  const { data } = await api.delete(
    `/api/marketing/youtube/playlists/${playlistId}/items/${itemId}`,
  );
  return data as { ok: boolean; deleted: boolean; playlist_item_id: string; note?: string };
}

export async function reorderYouTubePlaylistItem(
  playlistId: string,
  itemId: string,
  position: number,
): Promise<{ ok: boolean; item: YouTubePlaylistItem; quota?: YouTubeQuota }> {
  const { data } = await api.post(
    `/api/marketing/youtube/playlists/${playlistId}/items/${itemId}/reorder`,
    { position },
  );
  return data as { ok: boolean; item: YouTubePlaylistItem; quota?: YouTubeQuota };
}

/* ---------------------------------------------------------------------------
   Captions & languages
   ---------------------------------------------------------------------------
   Honest boundaries: the API cannot translate captions and cannot edit a
   track's text — the screen offers upload / rename / delete / download and
   says so, instead of showing a dead "Translate" button.
   ------------------------------------------------------------------------- */

export type YouTubeCaptionTrack = {
  caption_id: string;
  video_id: string;
  language: string;
  name: string;
  track_kind: string;
  audio_track_type: string;
  is_cc: boolean;
  is_draft: boolean;
  is_auto_synced: boolean;
  is_large: boolean;
  status: string;
  failure_reason: string;
  last_updated: string;
};

export type YouTubeCaptionVideo = {
  video_id: string;
  title: string;
  thumbnail_url: string;
  duration_seconds: number;
  published_at: string;
  privacy_status: string;
  classification?: string;
  has_captions: boolean;
};

export async function fetchYouTubeCaptionVideos(
  params: { limit?: number; page_token?: string } = {},
): Promise<{
  ok: boolean;
  items: YouTubeCaptionVideo[];
  count: number;
  next_page_token: string;
  has_more: boolean;
  note?: string;
  quota?: YouTubeQuota;
}> {
  const { data } = await api.get("/api/marketing/youtube/captions/videos", { params });
  return data as {
    ok: boolean;
    items: YouTubeCaptionVideo[];
    count: number;
    next_page_token: string;
    has_more: boolean;
    note?: string;
    quota?: YouTubeQuota;
  };
}

export async function fetchYouTubeCaptionLanguages(): Promise<{
  ok: boolean;
  items: { language: string; name: string }[];
  count: number;
}> {
  const { data } = await api.get("/api/marketing/youtube/captions/languages");
  return data as { ok: boolean; items: { language: string; name: string }[]; count: number };
}

export async function fetchYouTubeCaptions(
  videoId: string,
): Promise<{
  ok: boolean;
  video_id: string;
  items: YouTubeCaptionTrack[];
  count: number;
  supported_formats: string[];
  download_formats: string[];
  translation_available: boolean;
  translation_note?: string;
  quota?: YouTubeQuota;
}> {
  const { data } = await api.get(`/api/marketing/youtube/captions/video/${videoId}`);
  return data as {
    ok: boolean;
    video_id: string;
    items: YouTubeCaptionTrack[];
    count: number;
    supported_formats: string[];
    download_formats: string[];
    translation_available: boolean;
    translation_note?: string;
    quota?: YouTubeQuota;
  };
}

export async function uploadYouTubeCaption(body: {
  video_id: string;
  language: string;
  name?: string;
  source_url?: string;
  media_upload_id?: string;
  is_draft?: boolean;
}): Promise<{ ok: boolean; caption: YouTubeCaptionTrack; source?: string; quota?: YouTubeQuota }> {
  const { data } = await api.post("/api/marketing/youtube/captions", body);
  return data as {
    ok: boolean;
    caption: YouTubeCaptionTrack;
    source?: string;
    quota?: YouTubeQuota;
  };
}

export async function updateYouTubeCaption(
  captionId: string,
  body: { language?: string; name?: string; is_draft?: boolean },
): Promise<{ ok: boolean; caption: YouTubeCaptionTrack; note?: string; quota?: YouTubeQuota }> {
  const { data } = await api.patch(`/api/marketing/youtube/captions/${captionId}`, body);
  return data as {
    ok: boolean;
    caption: YouTubeCaptionTrack;
    note?: string;
    quota?: YouTubeQuota;
  };
}

export async function deleteYouTubeCaption(
  captionId: string,
): Promise<{ ok: boolean; deleted: boolean; caption_id: string }> {
  const { data } = await api.delete(`/api/marketing/youtube/captions/${captionId}`);
  return data as { ok: boolean; deleted: boolean; caption_id: string };
}

export async function downloadYouTubeCaption(
  captionId: string,
  tfmt: "srt" | "vtt" | "sbv" = "srt",
): Promise<{ ok: boolean; caption_id: string; format: string; text: string; bytes: number }> {
  const { data } = await api.get(`/api/marketing/youtube/captions/${captionId}/download`, {
    params: { tfmt },
  });
  return data as {
    ok: boolean;
    caption_id: string;
    format: string;
    text: string;
    bytes: number;
  };
}

/* ---------------------------------------------------------------------------
   Community moderation
   ---------------------------------------------------------------------------
   Moderation only works on comments on videos the connected channel owns;
   `comments.setModerationStatus` and `comments.delete` both need the
   manage permission, which the connection reports through `features`.
   ------------------------------------------------------------------------- */

export type YouTubeModerationItem = YouTubeComment & {
  thread_id: string;
  video_id: string;
  moderation_status: string;
  reply_count: number;
  has_replies: boolean;
};

export type YouTubeModerationStatus = "published" | "heldForReview" | "rejected";

export async function fetchYouTubeModerationQueue(
  params: { status?: YouTubeModerationStatus; max_results?: number; page_token?: string } = {},
): Promise<{
  ok: boolean;
  items: YouTubeModerationItem[];
  count: number;
  moderation_status: string;
  next_page_token: string;
  supported_statuses: string[];
  quota?: YouTubeQuota;
}> {
  const { data } = await api.get("/api/marketing/youtube/moderation/queue", { params });
  return data as {
    ok: boolean;
    items: YouTubeModerationItem[];
    count: number;
    moderation_status: string;
    next_page_token: string;
    supported_statuses: string[];
    quota?: YouTubeQuota;
  };
}

export type YouTubeModerationSummary = {
  ok: boolean;
  heldForReview: number | null;
  rejected: number | null;
  published: number | null;
  heldForReview_has_more?: boolean;
  rejected_has_more?: boolean;
  published_has_more?: boolean;
  heldForReview_reason?: string;
  rejected_reason?: string;
  published_reason?: string;
  quota?: YouTubeQuota;
};

export async function fetchYouTubeModerationSummary(): Promise<YouTubeModerationSummary> {
  const { data } = await api.get("/api/marketing/youtube/moderation/summary");
  return data as YouTubeModerationSummary;
}

export async function setYouTubeModerationStatus(body: {
  comment_ids: string[];
  status: YouTubeModerationStatus;
  ban_author?: boolean;
}): Promise<{
  ok: boolean;
  updated: number;
  moderation_status: string;
  ban_author: boolean;
  comment_ids: string[];
  quota?: YouTubeQuota;
}> {
  const { data } = await api.post("/api/marketing/youtube/moderation/status", body);
  return data as {
    ok: boolean;
    updated: number;
    moderation_status: string;
    ban_author: boolean;
    comment_ids: string[];
    quota?: YouTubeQuota;
  };
}

export async function deleteYouTubeComment(
  commentId: string,
): Promise<{ ok: boolean; deleted: boolean; comment_id: string; quota?: YouTubeQuota }> {
  const { data } = await api.delete(`/api/marketing/youtube/moderation/${commentId}`);
  return data as { ok: boolean; deleted: boolean; comment_id: string; quota?: YouTubeQuota };
}

export type OAuthTokenHealth = {
  ok: boolean;
  provider: string;
  label?: string;
  status: string;
  health: string;
  mode?: string | null;
  has_refresh?: boolean;
  token_expires_at?: string | null;
  seconds_to_expiry?: number | null;
  needs_reconnect?: boolean;
  can_refresh?: boolean;
  live_creds?: boolean;
};

export type OAuthHealthSummary = {
  ok: boolean;
  items: OAuthTokenHealth[];
  counts: Record<string, number>;
  hardening: Record<string, unknown>;
};

export async function fetchOAuthHealth(): Promise<OAuthHealthSummary> {
  const { data } = await api.get("/api/marketing/integrations/oauth/health");
  return data as OAuthHealthSummary;
}

export async function refreshOAuthTokens(provider: string): Promise<{
  ok: boolean;
  provider: string;
  health: OAuthTokenHealth;
}> {
  const { data } = await api.post(`/api/marketing/integrations/oauth/${provider}/refresh`);
  return data as { ok: boolean; provider: string; health: OAuthTokenHealth };
}

export async function revokeOAuth(
  provider: string,
  callVendor = true,
): Promise<{ ok: boolean; provider: string; health: OAuthTokenHealth }> {
  const { data } = await api.post(`/api/marketing/integrations/oauth/${provider}/revoke`, null, {
    params: { call_vendor: callVendor },
  });
  return data as { ok: boolean; provider: string; health: OAuthTokenHealth };
}

export async function fetchMyPermissions(): Promise<MarketingPermissions> {
  const { data } = await api.get("/api/marketing/permissions/me");
  return data as MarketingPermissions;
}

export type RoiSummary = {
  providers: Array<{
    provider: string;
    spend: number;
    revenue: number;
    conversions: number;
    clicks: number;
    roas: number;
  }>;
  totals: { spend: number; revenue: number; roas: number };
  series: Array<Record<string, unknown>>;
  generated_at: string;
};

export type ConnectorSyncResult = {
  provider: string;
  mode: string;
  reason?: string;
  detail?: string | null;
  message?: string;
  refreshed: boolean;
  totals: { spend: number; clicks: number; impressions: number; conversions: number; revenue: number; roas: number };
  synced_at: string;
};

export async function fetchAdsRoi(): Promise<RoiSummary> {
  const { data } = await api.get("/api/marketing/ads/roi");
  return data as RoiSummary;
}

export async function syncIntegration(provider: string): Promise<ConnectorSyncResult> {
  const { data } = await api.post(`/api/marketing/integrations/${provider}/sync`);
  return data as ConnectorSyncResult;
}

export async function updateIntegrationAccount(
  provider: string,
  input: {
    account_id?: string;
    property_id?: string;
    site_url?: string;
    ad_account_id?: string;
    customer_id?: string;
    developer_token?: string;
    username?: string;
    page_id?: string;
    threads_user_id?: string;
    ig_user_id?: string;
    ig_image_url?: string;
    author_urn?: string;
    member_urn?: string;
    organization_urn?: string;
    li_image_url?: string;
    li_video_url?: string;
    tw_image_url?: string;
    tw_video_url?: string;
    fb_image_url?: string;
    fb_video_url?: string;
    threads_image_url?: string;
    ig_video_url?: string;
    threads_video_url?: string;
  },
): Promise<Integration> {
  const { data } = await api.patch(`/api/marketing/integrations/${provider}/account`, input);
  return data as Integration;
}

export type Ga4Property = {
  property_id: string;
  property_name?: string;
  display_name: string;
  account_name: string;
};

export type Ga4PropertiesResponse = {
  ok: boolean;
  reason: string;
  detail?: string;
  properties: Ga4Property[];
  selected?: string;
};

/**
 * GA4 properties this workspace's own Google connection can read.
 *
 * The backend discovers them with the tenant's OAuth token, so the list is
 * always scoped to this workspace — a user never types a property id and can
 * never see another tenant's properties.
 */
export async function fetchGa4Properties(): Promise<Ga4PropertiesResponse> {
  const { data } = await api.get("/api/marketing/integrations/google_analytics/properties");
  return {
    ok: Boolean(data?.ok),
    reason: String(data?.reason || ""),
    detail: data?.detail ? String(data.detail) : undefined,
    properties: (data?.properties || []) as Ga4Property[],
    selected: data?.selected ? String(data.selected) : undefined,
  };
}

export type GscSite = {
  site_url: string;
  host: string;
  is_domain_property: boolean;
  /** The entry the automatic default would choose (main domain first). */
  is_default: boolean;
};

export type GscSitesResponse = {
  ok: boolean;
  reason: string;
  detail?: string;
  sites: GscSite[];
  selected?: string;
};

/**
 * Search Console properties this workspace's own Google connection can verify.
 *
 * Discovered with the tenant's OAuth token and ordered main-domain first, so the
 * list is always scoped to this workspace and a sub-domain never hides the root
 * property.
 */
export async function fetchGscSites(): Promise<GscSitesResponse> {
  const { data } = await api.get("/api/marketing/integrations/google_search_console/sites");
  return {
    ok: Boolean(data?.ok),
    reason: String(data?.reason || ""),
    detail: data?.detail ? String(data.detail) : undefined,
    sites: (data?.sites || []) as GscSite[],
    selected: data?.selected ? String(data.selected) : undefined,
  };
}

export type ValidateAccountResult = {
  ok: boolean;
  provider: string;
  reason?: string;
  mode?: string;
  account_id?: string;
  display_name?: string;
  validated_at?: string;
  detail?: string;
  hint?: string;
  /** Meta (Ads + Social): which configured surface was verified. */
  capability?: string;
  verified_surfaces?: string[];
  surfaces?: Record<
    string,
    {
      ok?: boolean;
      reason?: string;
      hint?: string;
      display_name?: string;
      /** Instagram Login surface details (profile / media / comments / mentions / publishing). */
      account_type?: string;
      media_id?: string;
      media_count?: number;
      comments?: number;
      mentions?: number;
      quota_usage?: number;
      quota_total?: number;
    }
  >;
  token_scopes?: {
    ok?: boolean;
    granted?: string[];
    instagram_manage_messages?: boolean;
    pages_manage_posts?: boolean;
  };
  /** Threads: publishing quota reported by /threads_publishing_limit. */
  quota_usage?: number | null;
  quota_total?: number | null;
  /** Threads insights require threads_manage_insights (not requested). */
  insights_supported?: boolean;
  insights_reason?: string;
};

export async function validateIntegration(provider: string): Promise<ValidateAccountResult> {
  const { data } = await api.post(`/api/marketing/integrations/${provider}/validate`);
  return data as ValidateAccountResult;
}

// ---------------------------------------------------------------------------
// Connected client websites (universal blog publishing)
// ---------------------------------------------------------------------------
export type ConnectedWebsiteStatus = "ACTIVE" | "FAILED" | "DISCONNECTED";
export type ConnectedWebsitePlatform =
  | "WORDPRESS"
  | "WEBFLOW"
  | "CUSTOM_API"
  | "UNKNOWN";

export type WebsiteDetection = {
  ok: boolean;
  status?: ConnectedWebsiteStatus;
  platform_type?: ConnectedWebsitePlatform;
  endpoint_url?: string;
  http_status?: number;
  reason?: string;
  detail?: string;
  candidates?: { path: string; http_status: number; reason?: string }[];
};

export type ConnectedWebsite = {
  id: string;
  company_id: string;
  target_url: string;
  platform_type: ConnectedWebsitePlatform;
  endpoint_url: string;
  auth_header_name: string;
  auth_scheme: string;
  status: ConnectedWebsiteStatus;
  has_api_key: boolean;
  api_key_preview: string;
  last_checked_at: string | null;
  last_publish_at: string | null;
  last_error: string;
  meta: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
};

export type ConnectWebsiteInput = {
  target_url: string;
  api_key: string;
  /** WordPress login, paired with a bare Application Password. */
  username?: string;
  auth_header_name?: string;
  auth_scheme?: string;
  endpoint_url?: string;
  platform_type?: string;
  extra_headers?: Record<string, string>;
};

export type ConnectedWebsitesResponse = {
  ok: boolean;
  items: ConnectedWebsite[];
  platforms: ConnectedWebsitePlatform[];
  receiver_paths: string[];
  auth_schemes: string[];
};

export type ConnectWebsiteResult = ConnectedWebsite & {
  ok: boolean;
  detection: WebsiteDetection;
};

export type WebsitePublishResult = {
  ok: boolean;
  website: ConnectedWebsite;
  endpoint_url: string;
  platform_type: ConnectedWebsitePlatform;
  http_status: number;
  attempts: number;
  reason: string;
  detail?: string;
  location?: string;
  payload?: Record<string, unknown>;
  url?: string;
  draft_id?: string;
};

export async function fetchConnectedWebsites(): Promise<ConnectedWebsitesResponse> {
  const { data } = await api.get("/api/marketing/websites");
  return data as ConnectedWebsitesResponse;
}

export async function connectWebsite(
  input: ConnectWebsiteInput,
): Promise<ConnectWebsiteResult> {
  const { data } = await api.post("/api/marketing/websites/connect", input);
  return data as ConnectWebsiteResult;
}

export async function testConnectedWebsite(id: string): Promise<ConnectWebsiteResult> {
  const { data } = await api.post(
    `/api/marketing/websites/${encodeURIComponent(id)}/test`,
  );
  return data as ConnectWebsiteResult;
}

export async function disconnectConnectedWebsite(
  id: string,
): Promise<{ ok: boolean; id: string }> {
  const { data } = await api.post(
    `/api/marketing/websites/${encodeURIComponent(id)}/disconnect`,
  );
  return data as { ok: boolean; id: string };
}

export async function publishBlogToConnectedWebsite(
  id: string,
  input: {
    draft_id?: string;
    title?: string;
    slug?: string;
    content?: string;
    excerpt?: string;
    featured_image?: string;
    published_at?: string;
    tags?: string[];
  },
): Promise<WebsitePublishResult> {
  const { data } = await api.post(
    `/api/marketing/websites/${encodeURIComponent(id)}/publish`,
    input,
  );
  return data as WebsitePublishResult;
}

export async function publishDraftToConnectedWebsite(
  id: string,
  draftId: string,
): Promise<WebsitePublishResult> {
  return publishBlogToConnectedWebsite(id, { draft_id: draftId });
}

/** Instagram Business account resolved from Meta (Page → linked IG account). */
export type MetaInstagramAccount = {
  provider: string;
  connected: boolean;
  page_id?: string;
  page_id_source?: string;
  page_name?: string;
  ig_user_id?: string;
  unverified_ig_user_id?: string;
  ig_user_id_source?: string;
  ig_username?: string;
  ig_resolved_at?: string;
  ig_resolve_source?: string;
  ig_resolve_note?: string;
  ig_resolve_error?: string;
  /** Meta's Page→IG link returned the account (not a configured/env fallback). */
  meta_verified?: boolean;
  /** Meta confirmed the id — via the link or a direct Graph lookup. */
  ig_verified?: boolean;
  ig_verify_source?: string;
  ig_verify_error?: string;
  verification?: {
    ig_user_id?: string;
    checked?: boolean;
    ok?: boolean;
    username?: string;
    status?: number;
    error?: string;
    source?: string;
  };
};

export async function fetchMetaInstagramAccount(): Promise<MetaInstagramAccount> {
  const { data } = await api.get("/api/marketing/integrations/meta_ads/instagram");
  return data as MetaInstagramAccount;
}

/** Ask Meta which Instagram Business account is linked to the connected Page. */
export async function resolveMetaAccounts(): Promise<MetaInstagramAccount> {
  const { data } = await api.post("/api/marketing/integrations/meta_ads/resolve-accounts");
  return data as MetaInstagramAccount;
}

export async function fetchIntegrationMetrics(provider?: string): Promise<Array<Record<string, unknown>>> {
  const { data } = await api.get("/api/marketing/integrations/metrics", {
    params: provider ? { provider } : {},
  });
  return (data?.items || []) as Array<Record<string, unknown>>;
}

export type ProductAnalyticsEventName = "page_view" | "feature_use" | "session_start";

export type ProductAnalyticsSummary = {
  tenant_id: string;
  generated_at: string;
  days: number;
  totals: {
    events: number;
    page_views: number;
    feature_uses: number;
    sessions: number;
  };
  by_name: Array<{ name: string; count: number }>;
  top_pages: Array<{ path: string; views: number }>;
  series: Array<{
    day: string;
    page_views: number;
    feature_uses: number;
    sessions: number;
    total: number;
  }>;
};

export async function trackProductEvents(
  events: Array<{ name: ProductAnalyticsEventName | string; path?: string; props?: Record<string, string> }>,
): Promise<{ ok: boolean; accepted: number; skipped: number }> {
  const { data } = await api.post("/api/marketing/product-analytics/events", { events });
  return data as { ok: boolean; accepted: number; skipped: number };
}

export async function fetchProductAnalyticsSummary(days = 14): Promise<ProductAnalyticsSummary> {
  const { data } = await api.get("/api/marketing/product-analytics/summary", { params: { days } });
  return data as ProductAnalyticsSummary;
}

// ---------------------------------------------------------------------------
// Backlink sites directory (read-only listing — MarketIQ publishes nothing here)
// ---------------------------------------------------------------------------

export type BacklinkSite = {
  id: string;
  rank: number;
  name: string;
  url: string;
  best_for: string;
  type: string;
  type_label: string;
  industries: string[];
  industry_labels: string[];
  country: string;
  country_label: string;
};

export type BacklinkFilterOption = { id: string; label: string; count: number };

export type BacklinkSitesResponse = {
  items: BacklinkSite[];
  total: number;
  count: number;
  filters: { industries: BacklinkFilterOption[]; countries: BacklinkFilterOption[] };
  applied: { industry: string; country: string };
  note?: string;
};

export async function fetchBacklinkSites(params?: {
  industry?: string;
  country?: string;
}): Promise<BacklinkSitesResponse> {
  const { data } = await api.get("/api/marketing/backlinks/sites", {
    params: {
      ...(params?.industry ? { industry: params.industry } : {}),
      ...(params?.country ? { country: params.country } : {}),
    },
  });
  return {
    items: (data?.items || []) as BacklinkSite[],
    total: Number(data?.total || 0),
    count: Number(data?.count || 0),
    filters: {
      industries: (data?.filters?.industries || []) as BacklinkFilterOption[],
      countries: (data?.filters?.countries || []) as BacklinkFilterOption[],
    },
    applied: {
      industry: String(data?.applied?.industry || ""),
      country: String(data?.applied?.country || ""),
    },
    note: data?.note ? String(data.note) : undefined,
  };
}

export type NewsDomain = { id: string; label: string };

export type NewsProviderStatus = { id: string; status: "ok" | "skipped" | "unavailable" | string };

export type NewsProviderCatalogItem = { id: string; configured: boolean };

export type NewsIntelligenceHealth = {
  ok: boolean;
  module?: string;
  default_count?: number;
  maximum_count?: number;
  providers: NewsProviderCatalogItem[];
};

export type NewsArticle = {
  id: string;
  title: string;
  description: string;
  url: string;
  source_name: string;
  source_url?: string;
  author?: string;
  published_at?: string;
  image_url?: string;
  language?: string;
  country?: string;
  category?: string;
  domain?: string;
  keywords?: string[];
  source_count?: number;
  providers?: string[];
  relevance_score?: number;
  credibility_score?: number;
  freshness_score?: number;
  trending_score?: number;
  ranking_score?: number;
  /** Present when backend verified complete article content. */
  content_complete?: boolean;
  content_status?: string;
  body_char_count?: number;
  extraction_source?: string;
  extraction_confidence?: number;
  /** Verified publisher URL (preferred for View Source Article). */
  canonical_source_url?: string;
  /** Canonical body when loaded from Article Details. */
  body?: string;
};

export type NewsFetchResult = {
  ok: boolean;
  domain: NewsDomain;
  requested_count: number;
  maximum_count: number;
  returned_count: number;
  available_count: number;
  verified_count?: number;
  discovered_count?: number;
  cached?: boolean;
  cache_miss?: boolean;
  message?: string | null;
  providers: NewsProviderStatus[];
  items: NewsArticle[];
};

export async function fetchNewsDomains(): Promise<NewsDomain[]> {
  const { data } = await api.get("/api/marketing/news-intelligence/domains");
  return (data?.items || []) as NewsDomain[];
}

export async function fetchNewsIntelligenceHealth(): Promise<NewsIntelligenceHealth> {
  const { data } = await api.get("/api/marketing/news-intelligence/health");
  return data as NewsIntelligenceHealth;
}

export async function fetchNewsIntelligence(input: {
  domain: string;
  requested_count?: number;
  refresh?: boolean;
  /** When true, return cache hit or empty miss — never call providers. */
  cache_only?: boolean;
}): Promise<NewsFetchResult> {
  const { data } = await api.post(
    "/api/marketing/news-intelligence/fetch",
    {
      domain: input.domain,
      requested_count: input.requested_count,
      refresh: input.refresh ?? false,
      cache_only: input.cache_only ?? false,
    },
    { timeout: 180000 },
  );
  return data as NewsFetchResult;
}

export async function fetchPersistedNews(input: {
  domain: string;
  requested_count?: number;
}): Promise<NewsFetchResult> {
  const { data } = await api.get("/api/marketing/news-intelligence/articles", {
    params: {
      domain: input.domain,
      requested_count: input.requested_count,
    },
  });
  return data as NewsFetchResult;
}

export async function clearPersistedNews(domain: string): Promise<{ ok: boolean; deleted: number }> {
  const { data } = await api.delete("/api/marketing/news-intelligence/articles", {
    params: { domain },
  });
  return data as { ok: boolean; deleted: number };
}

export type NewsSchedule = {
  id?: string | null;
  tenant_id?: string;
  enabled: boolean;
  domain_id: string;
  hour_local: number;
  minute_local: number;
  timezone: string;
  last_run_at?: string | null;
  last_run_date_local?: string | null;
  meta?: Record<string, unknown>;
};

export async function fetchNewsSchedule(): Promise<NewsSchedule> {
  const { data } = await api.get("/api/marketing/news-intelligence/schedule");
  return (data?.schedule || data) as NewsSchedule;
}

export async function updateNewsSchedule(input: {
  enabled?: boolean;
  domain_id?: string;
  hour_local?: number;
  minute_local?: number;
  timezone?: string;
}): Promise<NewsSchedule> {
  const { data } = await api.put("/api/marketing/news-intelligence/schedule", input);
  return (data?.schedule || data) as NewsSchedule;
}

export type NewsArticleDetails = {
  id: string;
  title: string;
  publisher?: string;
  source_name?: string;
  published_at?: string;
  image_url?: string;
  canonical_url?: string;
  /** Verified publisher URL for View Source Article (never Google News wrapper). */
  canonical_source_url?: string;
  source_url?: string;
  url?: string;
  body: string;
  description?: string;
  extraction_source?: string;
  extraction_status?: string;
  extraction_confidence?: number;
  body_char_count?: number;
  content_complete: boolean;
};

export async function fetchNewsArticleDetails(articleId: string): Promise<NewsArticleDetails> {
  const { data } = await api.get(`/api/marketing/news-intelligence/articles/${encodeURIComponent(articleId)}`, {
    timeout: 30000,
  });
  return (data?.article || data) as NewsArticleDetails;
}

export type BusinessKnowledgeProfile = {
  id?: string;
  tenant_id?: string;
  company_name: string;
  industry: string;
  services: string;
  target_audience: string;
  positioning: string;
  brand_voice_notes: string;
  notes: string;
  meta?: Record<string, unknown>;
  updated_at?: string;
};

export type BusinessDocument = {
  id: string;
  title: string;
  filename?: string;
  mime_type?: string;
  char_count?: number;
  preview?: string;
  created_at?: string;
  updated_at?: string;
};

export type BusinessKnowledgePayload = {
  ok: boolean;
  ready: boolean;
  profile: BusinessKnowledgeProfile;
  documents: BusinessDocument[];
  document_count: number;
};

export async function fetchBusinessKnowledge(): Promise<BusinessKnowledgePayload> {
  const { data } = await api.get("/api/marketing/business-knowledge");
  return data as BusinessKnowledgePayload;
}

export async function saveBusinessKnowledge(
  patch: Partial<BusinessKnowledgeProfile>,
): Promise<{ ok: boolean; ready: boolean; profile: BusinessKnowledgeProfile }> {
  const { data } = await api.put("/api/marketing/business-knowledge", patch);
  return data as { ok: boolean; ready: boolean; profile: BusinessKnowledgeProfile };
}

export async function pasteBusinessDocument(input: {
  title: string;
  text: string;
}): Promise<BusinessDocument> {
  const { data } = await api.post("/api/marketing/business-knowledge/documents", input);
  return (data?.document || data) as BusinessDocument;
}

export async function uploadBusinessDocument(file: File, title?: string): Promise<BusinessDocument> {
  const form = new FormData();
  form.append("file", file);
  if (title) form.append("title", title);
  const { data } = await api.post("/api/marketing/business-knowledge/documents/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return (data?.document || data) as BusinessDocument;
}

export async function deleteBusinessDocument(docId: string): Promise<void> {
  await api.delete(`/api/marketing/business-knowledge/documents/${docId}`);
}

export type NewsChatMessage = {
  role: "user" | "assistant" | "system";
  content: string;
  is_revision?: boolean;
};

export type NewsGenerateResult = {
  ok: boolean;
  content_type: string;
  content?: string;
  title: string;
  body: string;
  reply?: string;
  is_revision?: boolean;
  conversation_history?: NewsChatMessage[];
  sources: Array<{ title: string; source_name?: string; url?: string; published_at?: string }>;
  selected_articles?: Array<{ id?: string; title: string; url?: string; source_name?: string }>;
  business_context_used?: boolean;
  ai_source?: string;
  draft?: { id: string; title: string; channel: string; body: string; status: string; meta?: Record<string, unknown> };
  business?: { company_name?: string; industry?: string; excerpts_used?: number };
  message?: string;
};

export async function generateNewsContent(input: {
  articles: Array<
    Pick<NewsArticle, "id" | "title" | "description" | "url" | "source_name" | "published_at"> & {
      body?: string;
    }
  >;
  content_type?: "master_content" | "linkedin" | "blog" | "newsletter";
  save_draft?: boolean;
  user_message?: string;
  conversation_history?: NewsChatMessage[];
}): Promise<NewsGenerateResult> {
  const { data } = await api.post(
    "/api/marketing/news-intelligence/generate",
    {
      articles: input.articles,
      content_type: input.content_type ?? "master_content",
      save_draft: input.save_draft ?? true,
      user_message: input.user_message,
      conversation_history: input.conversation_history,
    },
    { timeout: 120000 },
  );
  return data as NewsGenerateResult;
}

export type PublishingChannel = {
  id: string;
  label: string;
  category: string;
  adaptable: boolean;
  publish_ready: boolean;
  status: "available" | "future" | string;
};

export type PublishingChannelCatalog = {
  ok: boolean;
  available: PublishingChannel[];
  future: PublishingChannel[];
  note?: string;
};

export async function fetchNewsPublishingChannels(): Promise<PublishingChannelCatalog> {
  const { data } = await api.get("/api/marketing/news-intelligence/publishing-channels");
  return data as PublishingChannelCatalog;
}

export type NewsAdaptVariant = {
  channel: string;
  label: string;
  publish_ready?: boolean;
  body: string;
  ai_source?: string;
  draft?: { id: string; channel: string } | null;
};

export type NewsAdaptResult = {
  ok: boolean;
  master_draft_id: string;
  variants: NewsAdaptVariant[];
  skipped_future?: string[];
  unknown?: string[];
  message?: string;
};

export async function adaptNewsMasterContent(input: {
  draft_id: string;
  channels: string[];
  save_drafts?: boolean;
}): Promise<NewsAdaptResult> {
  const { data } = await api.post(
    "/api/marketing/news-intelligence/adapt",
    {
      draft_id: input.draft_id,
      channels: input.channels,
      save_drafts: input.save_drafts ?? true,
    },
    { timeout: 180000 },
  );
  return data as NewsAdaptResult;
}

export type CreatePostSession = {
  id: string;
  tenant_id: string;
  article_id: string;
  article_title: string;
  article_snapshot: {
    id: string;
    title: string;
    body: string;
    description?: string;
    url?: string;
    canonical_url?: string;
    source_name?: string;
    publisher?: string;
    published_at?: string;
    image_url?: string;
    body_char_count?: number;
    content_complete?: boolean;
  };
  bk_snapshot: {
    company_name?: string;
    industry?: string;
    services?: string;
    positioning?: string;
    brand_voice_notes?: string;
    ready?: boolean;
  };
  chat_history: NewsChatMessage[];
  master_content: {
    title?: string;
    body?: string;
    draft?: { id: string; title: string; channel: string; body: string; status: string };
    sources?: Array<{ title: string; source_name?: string; url?: string }>;
    relevance?: Record<string, unknown>;
    claim_count?: number;
  };
  selected_channels: string[];
  adapt_result?: NewsAdaptResult;
  status: "active" | "saved" | "expired";
  expires_at: string;
  created_at: string;
  updated_at: string;
  time_remaining_seconds?: number;
  is_expired?: boolean;
  meta?: Record<string, unknown>;
};

export async function fetchActiveCreatePostSessions(): Promise<{
  ok: boolean;
  sessions: CreatePostSession[];
  count: number;
}> {
  const { data } = await api.get("/api/marketing/news-intelligence/sessions/active");
  return data as { ok: boolean; sessions: CreatePostSession[]; count: number };
}

export async function createOrGetCreatePostSession(input: {
  article_id?: string;
  article_snapshot?: Record<string, unknown>;
  bk_snapshot?: Record<string, unknown>;
  selected_channels?: string[];
  force_new?: boolean;
}): Promise<{ ok: boolean; session: CreatePostSession }> {
  const { data } = await api.post("/api/marketing/news-intelligence/sessions", input);
  return data as { ok: boolean; session: CreatePostSession };
}

export async function fetchCreatePostSession(sessionId: string): Promise<{
  ok: boolean;
  session: CreatePostSession;
}> {
  const { data } = await api.get(`/api/marketing/news-intelligence/sessions/${encodeURIComponent(sessionId)}`);
  return data as { ok: boolean; session: CreatePostSession };
}

export async function sendSessionChatMessage(
  sessionId: string,
  input: { user_message?: string; selected_channels?: string[] },
): Promise<{ ok: boolean; session: CreatePostSession; result: NewsGenerateResult }> {
  const { data } = await api.post(
    `/api/marketing/news-intelligence/sessions/${encodeURIComponent(sessionId)}/message`,
    input,
    { timeout: 120000 },
  );
  return data as { ok: boolean; session: CreatePostSession; result: NewsGenerateResult };
}

export async function adaptSessionContent(
  sessionId: string,
  channels: string[],
): Promise<NewsAdaptResult> {
  const { data } = await api.post(
    `/api/marketing/news-intelligence/sessions/${encodeURIComponent(sessionId)}/adapt`,
    { channels },
    { timeout: 180000 },
  );
  return data as NewsAdaptResult;
}

export async function saveSessionToStudio(sessionId: string): Promise<{
  ok: boolean;
  draft_id: string;
  session_id: string;
  message: string;
}> {
  const { data } = await api.post(
    `/api/marketing/news-intelligence/sessions/${encodeURIComponent(sessionId)}/save-to-studio`,
  );
  return data as { ok: boolean; draft_id: string; session_id: string; message: string };
}

export async function deleteCreatePostSession(sessionId: string): Promise<{
  ok: boolean;
  deleted: boolean;
}> {
  const { data } = await api.delete(
    `/api/marketing/news-intelligence/sessions/${encodeURIComponent(sessionId)}`,
  );
  return data as { ok: boolean; deleted: boolean };
}

// ---------------------------------------------------------------------------
// MinIO Media Uploads (Multi-tenant isolated buckets)
// ---------------------------------------------------------------------------

export type MediaUploadRecord = {
  id: string;
  tenant_id: string;
  bucket: string;
  storage_key: string;
  filename: string;
  kind: "image" | "video" | "document" | "audio";
  mime_type: string;
  size_bytes: number;
  etag?: string | null;
  public_url: string;
  uploaded_by?: string | null;
  meta?: Record<string, unknown>;
  created_at?: string;
};

export async function uploadMediaFile(
  file: File,
  kind?: "image" | "video",
  onProgress?: (percent: number) => void,
): Promise<MediaUploadRecord> {
  const formData = new FormData();
  formData.append("file", file);
  if (kind) {
    formData.append("kind", kind);
  }
  const { data } = await api.post<{ ok: boolean; media: MediaUploadRecord }>(
    "/api/marketing/media/upload",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      },
      timeout: 180000,
    },
  );
  return data.media;
}

export async function fetchUploadedMedia(kind?: string): Promise<MediaUploadRecord[]> {
  const { data } = await api.get<{ ok: boolean; items: MediaUploadRecord[] }>(
    "/api/marketing/media/uploads",
    {
      params: kind ? { kind } : {},
    },
  );
  return data.items || [];
}

export async function deleteUploadedMedia(uploadId: string): Promise<boolean> {
  const { data } = await api.delete<{ ok: boolean; deleted: boolean }>(
    `/api/marketing/media/uploads/${encodeURIComponent(uploadId)}`,
  );
  return data.deleted;
}

